import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useAuth } from '../contexts/AuthContext';
import { API } from '@/lib/apiBase';
import { toast } from 'sonner';
import {
  Plus,
  Minus,
  Receipt,
  Settings2,
  Code2,
  Loader2,
  Trash2,
  Pencil,
  FilePlus2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  FileText,
  Paperclip,
  Eye,
  Layers,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { Combobox } from '../components/ui/combobox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const todayISO = () => new Date().toISOString().slice(0, 10);

const DEV_INVOICE_PRODUCT_NAME = 'Développement Web';
const DEV_INVOICE_CATEGORY_NAME = 'Logiciel / Informatique';

const DEV_INVOICE_CURRENCIES = [
  { value: 'EUR', label: '€ EUR' },
  { value: 'USD', label: '$ USD' },
];

const formatDevInvoiceMoney = (amount, currency = 'EUR') =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency || 'EUR' }).format(amount || 0);

const calculateEntryLineAmounts = (amountHt, vatRate) => {
  const ht = parseFloat(amountHt) || 0;
  const rate = parseFloat(vatRate) || 0;
  const vat = Math.round(ht * (rate / 100) * 100) / 100;
  const ttc = Math.round((ht + vat) * 100) / 100;
  return { amount_vat: vat, amount_ttc: ttc };
};

function pickSiegeEstablishment(company) {
  const etabs = company?.etablissements || [];
  if (!etabs.length) return { establishment_id: '', establishment_name: '' };
  const siege = etabs.find((e) => /si[eè]ge|siege\s+social/i.test(e.name || ''));
  const e = siege || etabs[0];
  return { establishment_id: e.id || '', establishment_name: e.name || '' };
}

function pickDirectionSector(sectors, establishmentId, companyId) {
  let list = (sectors || []).filter((s) => String(s.company_id || '') === String(companyId || ''));
  if (establishmentId) {
    const byEst = list.filter((s) => String(s.establishment_id || '') === String(establishmentId));
    if (byEst.length) list = byEst;
  }
  const dir = list.find((s) => /^direction/i.test((s.name || '').trim()));
  const pick = dir || list[0];
  if (!pick) return { sector_id: '', sector_name: '' };
  return { sector_id: pick.id, sector_name: pick.name || '' };
}

function matchCompanyForJournalLine(line, companies) {
  const cid = line.company_id != null ? String(line.company_id).trim() : '';
  if (cid) {
    const byId = companies.find((c) => String(c.id) === cid);
    if (byId) return byId;
  }
  const cname = (line.company_name || '').trim();
  if (cname) {
    const byName = companies.find((c) => (c.name || '').trim() === cname);
    if (byName) return byName;
  }
  return null;
}

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatMoneyPrint(amount, currency) {
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount || 0);
  } catch {
    return `${Number(amount || 0).toFixed(2)} ${currency || ''}`;
  }
}

function convertDevLineAmountsFromEur(line, invoiceCurrency, exchangeRateEurPerUnit) {
  const htEur = parseFloat(line.amount_ht) || 0;
  const vatEur = parseFloat(line.amount_vat) || 0;
  const ttcEur = parseFloat(line.amount_ttc) || 0;
  if (invoiceCurrency !== 'USD' || !exchangeRateEurPerUnit || exchangeRateEurPerUnit <= 0) {
    return { ht: htEur, vat: vatEur, ttc: ttcEur };
  }
  return {
    ht: Math.round((htEur / exchangeRateEurPerUnit) * 100) / 100,
    vat: Math.round((vatEur / exchangeRateEurPerUnit) * 100) / 100,
    ttc: Math.round((ttcEur / exchangeRateEurPerUnit) * 100) / 100,
  };
}

/** Heures affichées (fr-FR), ex. 2,5 */
function formatQtyHoursPrint(qty) {
  const n = Number(qty);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * Découpe une ligne d’entrée facture dev en lignes tableau : création / débogage / forfait MEP.
 * Les HT EUR sont réconciliées sur le total temps (hors MEP) pour coller à amount_ht.
 */
function expandDevEntryLineToInvoiceRows(line) {
  const product = (line.product_name || '').trim();
  const app = (line.application_name || '').trim();
  const baseDesc = [product, app ? `— ${app}` : ''].filter(Boolean).join(' ').trim() || '—';
  const baseEsc = escapeHtml(baseDesc);

  const ch = parseFloat(line.dev_creation_hours) || 0;
  const dh = parseFloat(line.dev_debug_hours) || 0;
  const rc = line.dev_creation_rate_eur;
  const rd = line.dev_debug_rate_eur;
  const mep = parseFloat(line.dev_mep_ht_eur) || 0;

  const sub = [];
  if (ch > 0 && rc != null && !Number.isNaN(rc)) {
    sub.push({
      kind: 'creation',
      qty: ch,
      puEur: rc,
      htEur: Math.round(ch * rc * 100) / 100,
    });
  }
  if (dh > 0 && rd != null && !Number.isNaN(rd)) {
    sub.push({
      kind: 'debug',
      qty: dh,
      puEur: rd,
      htEur: Math.round(dh * rd * 100) / 100,
    });
  }

  const timeHtTarget = Math.round(((parseFloat(line.amount_ht) || 0) - mep) * 100) / 100;
  const sumSub = sub.reduce((s, r) => s + r.htEur, 0);
  const diff = Math.round((timeHtTarget - sumSub) * 100) / 100;
  if (sub.length && Math.abs(diff) >= 0.001) {
    const last = sub[sub.length - 1];
    last.htEur = Math.round((last.htEur + diff) * 100) / 100;
    if (last.qty > 0) last.puEur = last.htEur / last.qty;
  }

  const rows = [];
  for (const r of sub) {
    const label = r.kind === 'creation' ? 'Création' : 'Débogage';
    rows.push({
      descriptionHtml: `${baseEsc}<br/><span class="desc-kind">${label}</span> (horaire)`,
      qty: r.qty,
      puEur: r.puEur,
      htEur: r.htEur,
      vat_rate: line.vat_rate,
    });
  }
  if (mep > 0) {
    rows.push({
      descriptionHtml: `${baseEsc}<br/><span class="desc-kind">Production / MEP</span> (forfait)`,
      qty: 1,
      puEur: mep,
      htEur: mep,
      vat_rate: line.vat_rate,
    });
  }
  if (!rows.length && (parseFloat(line.amount_ht) || 0) > 0) {
    const ht = parseFloat(line.amount_ht) || 0;
    rows.push({
      descriptionHtml: baseEsc,
      qty: 1,
      puEur: ht,
      htEur: ht,
      vat_rate: line.vat_rate,
    });
  }
  return rows;
}

/**
 * Alloue les montants devise facture par sous-ligne pour que la somme = convertDevLineAmountsFromEur(line).
 */
function assignInvoiceAmountsToExpandedRows(line, expanded, invoiceCurrency, exchangeRateEurPerUnit) {
  const lineInv = convertDevLineAmountsFromEur(line, invoiceCurrency, exchangeRateEurPerUnit);
  const totalHtEur = parseFloat(line.amount_ht) || 0;
  if (!expanded.length) return [];
  if (totalHtEur <= 0) {
    return expanded.map((r) => ({
      ...r,
      htInv: 0,
      puInv: 0,
      vatInv: 0,
      ttcInv: 0,
    }));
  }

  let allocatedHt = 0;
  let allocatedVat = 0;
  const vatRate = parseFloat(line.vat_rate) || 0;
  const out = expanded.map((r, i) => {
    let htInv;
    if (i < expanded.length - 1) {
      htInv = Math.round(lineInv.ht * (r.htEur / totalHtEur) * 100) / 100;
      allocatedHt += htInv;
    } else {
      htInv = Math.round((lineInv.ht - allocatedHt) * 100) / 100;
    }
    const puInv = r.qty > 0 ? Math.round((htInv / r.qty) * 10000) / 10000 : htInv;
    let vatInv;
    if (i < expanded.length - 1 && lineInv.ht > 0) {
      vatInv = Math.round(lineInv.vat * (htInv / lineInv.ht) * 100) / 100;
      allocatedVat += vatInv;
    } else {
      vatInv = Math.round((lineInv.vat - allocatedVat) * 100) / 100;
    }
    const ttcInv = Math.round((htInv + vatInv) * 100) / 100;
    return { ...r, htInv, puInv, vatInv, ttcInv, vat_rate: vatRate };
  });
  return out;
}

/** Lignes d’adresse depuis un objet société / établissement (champs Mongo usuels). */
function collectAddressLinesFromEntity(obj) {
  if (!obj || typeof obj !== 'object') return [];
  const l1 = String(obj.adresse || obj.address || obj.ligne1 || obj.ligne_1 || '').trim();
  const comp = String(obj.complement_adresse || '').trim();
  const cp = String(obj.code_postal || obj.postal_code || '').trim();
  const ville = String(obj.ville || obj.city || '').trim();
  const pays = String(obj.pays || obj.country || '').trim();
  const lines = [];
  if (l1) lines.push(l1);
  if (comp) lines.push(comp);
  const ligneVille = [cp, ville].filter(Boolean).join(' ');
  if (ligneVille) lines.push(pays ? `${ligneVille}, ${pays}` : ligneVille);
  else if (pays) lines.push(pays);
  return lines;
}

/** Colonne client = société(s) bénéficiaire(s), enrichies depuis /external/companies. */
function buildBeneficiaryColumnHtml(lines, companiesList) {
  if (!lines?.length) {
    return '<div class="addr-muted">—</div>';
  }
  const seen = new Set();
  const blocks = [];
  for (const l of lines) {
    const id = String(l.company_id || '').trim();
    const nm = (l.company_name || '').trim();
    const key = id || nm.toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const company =
      (id && companiesList?.find((c) => String(c.id) === id)) ||
      companiesList?.find((c) => (c.name || '').trim().toLowerCase() === nm.toLowerCase()) ||
      null;
    const displayName = (company?.name || nm || '—').trim();
    const etabs = company?.etablissements || [];
    const siege =
      etabs.find((e) => /si[eè]ge|siege\s+social/i.test(e.name || '')) || etabs[0];
    let addrLines = [];
    if (siege) addrLines = collectAddressLinesFromEntity(siege);
    if (!addrLines.length && company) addrLines = collectAddressLinesFromEntity(company);
    const extras = [];
    if (company?.email) extras.push(String(company.email).trim());
    if (company?.telephone || company?.tel) {
      extras.push(String(company.telephone || company.tel).trim());
    }
    if (company?.siret) extras.push(String(company.siret).trim());
    if (company?.siren && !company?.siret) extras.push(String(company.siren).trim());
    const detailLines = [...addrLines, ...extras];
    const body = detailLines.map((t) => escapeHtml(t)).join('<br/>');
    blocks.push(
      `<div class="client-card"><div class="client-name">${escapeHtml(displayName.toUpperCase())}</div>${
        body
          ? `<div class="client-detail">${body}</div>`
          : '<div class="addr-muted">Coordonnées complètes non renseignées en base pour cette société.</div>'
      }</div>`
    );
  }
  return blocks.length ? blocks.join('<div class="client-sep"></div>') : '<div class="addr-muted">—</div>';
}

const INVOICE_ISSUER_NAME = 'CG Developpement';
const INVOICE_ISSUER_LINES = [
  'Allée des prés rouets BP 36',
  '69510 MESSIMY, FR',
  'compta@prysme.eu',
  '88883325800021',
];

/**
 * HTML complet du document facture (aperçu, impression, capture pièce jointe).
 * @param exchangeRate — taux devise → EUR (comme Entrées / API exchange-rate).
 * @param includePreviewChrome — barre « Imprimer » (masquée à l’impression).
 */
function buildDevInvoicePreviewDocumentHtml(entryHeader, lines, options = {}, includePreviewChrome = true) {
  const { logoUrl = '', currency = 'EUR', exchangeRate = 1.0, companies: companiesList = [] } = options;
  const cur = currency === 'USD' ? 'USD' : 'EUR';
  const xr = cur === 'EUR' ? 1 : parseFloat(exchangeRate) || 1;
  const flatRows = [];
  for (const l of lines) {
    const expanded = expandDevEntryLineToInvoiceRows(l);
    const withInv = assignInvoiceAmountsToExpandedRows(l, expanded, cur, xr);
    flatRows.push(...withInv);
  }
  const tableRows = flatRows
    .map((r) => {
      const vatPct = r.vat_rate != null ? String(r.vat_rate) : '20';
      return `<tr>
        <td class="col-desc">${r.descriptionHtml}</td>
        <td class="col-qty c">${formatQtyHoursPrint(r.qty)}</td>
        <td class="col-pu r">${formatMoneyPrint(r.puInv, cur)}</td>
        <td class="col-tva c">${escapeHtml(vatPct)}</td>
        <td class="col-ht r">${formatMoneyPrint(r.htInv, cur)}</td>
      </tr>`;
    })
    .join('');
  let totalHt = 0;
  let totalVat = 0;
  let totalTtc = 0;
  lines.forEach((l) => {
    const c = convertDevLineAmountsFromEur(l, cur, xr);
    totalHt += c.ht;
    totalVat += c.vat;
    totalTtc += c.ttc;
  });
  const dateFr = entryHeader.invoice_date
    ? new Date(`${entryHeader.invoice_date}T12:00:00`).toLocaleDateString('fr-FR')
    : '—';
  const logoBlock = logoUrl
    ? `<img src="${escapeHtml(logoUrl)}" alt="Prysme" class="inv-logo" crossorigin="anonymous"/>`
    : '';
  const rateNote =
    cur === 'USD' && xr > 0
      ? `<p class="rate-note">Taux appliqué : 1 USD = ${xr.toFixed(4)} EUR</p>`
      : '';
  const issuerBody = INVOICE_ISSUER_LINES.map((t) => escapeHtml(t)).join('<br/>');
  const beneficiaryHtml = buildBeneficiaryColumnHtml(lines, companiesList);
  const legalMentions = `En cas de retard de paiement, une pénalité égale à trois fois le taux d’intérêt légal en vigueur sera exigible (art. L.441-6 C. com.). Indemnité forfaitaire pour frais de recouvrement : 40&nbsp;€ (art. D.441-5 C. com.).`;
  const toolbar = includePreviewChrome
    ? `<div class="preview-toolbar no-print">
        <button type="button" class="tb-btn" onclick="window.print()">Imprimer ou enregistrer en PDF</button>
      </div>`
    : '';
  const bodyPadding = includePreviewChrome ? '32px 40px 88px' : '32px 40px 32px';

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/><title>Facture ${escapeHtml(entryHeader.invoice_number)}</title>
<style>
  *{box-sizing:border-box;}
  body{font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;margin:0;padding:${bodyPadding};color:#111;background:#fff;max-width:920px;margin:0 auto;font-size:13px;line-height:1.5;}
  .inv-top{display:flex;align-items:flex-start;gap:20px;margin-bottom:8px;}
  .inv-logo{height:52px;width:auto;display:block;}
  .inv-title-wrap{flex:1;}
  h1.facture-title{margin:0 0 12px;font-size:28px;font-weight:800;letter-spacing:-0.02em;}
  .meta-block{font-size:13px;color:#222;}
  .meta-block div{margin-bottom:4px;}
  .meta-lbl{color:#444;}
  .addr-grid{display:grid;grid-template-columns:1fr 1fr;gap:32px;margin:28px 0 24px;padding-top:20px;border-top:1px solid #e2e8f0;}
  .issuer-name,.client-name{font-weight:800;font-size:14px;text-transform:uppercase;margin-bottom:8px;letter-spacing:0.02em;}
  .issuer-lines,.client-detail{font-size:13px;color:#222;line-height:1.55;}
  .desc-kind{font-weight:700;color:#334155;}
  .client-card + .client-sep{margin-top:16px;padding-top:16px;border-top:1px dashed #e2e8f0;}
  .addr-muted{color:#94a3b8;font-size:12px;}
  .rate-note{font-size:12px;color:#64748b;margin:0 0 16px;}
  table.inv-table{width:100%;border-collapse:collapse;font-size:13px;margin-top:8px;}
  .inv-table th{background:#3b6fb6;color:#fff;font-weight:600;padding:11px 10px;text-align:left;border:1px solid #2d5a94;}
  .inv-table th.c,.inv-table td.c{text-align:center;}
  .inv-table td{padding:10px;border:1px solid #93b4e0;vertical-align:top;}
  .inv-table td.r{text-align:right;}
  .col-desc{width:42%;}
  .col-qty{width:8%;}
  .col-pu{width:18%;}
  .col-tva{width:12%;}
  .col-ht{width:20%;}
  .sum-wrap{margin-top:20px;display:flex;justify-content:flex-end;}
  .sum-box{min-width:280px;}
  .sum-row{display:flex;justify-content:space-between;padding:6px 0;font-size:13px;border-bottom:1px solid #e2e8f0;}
  .sum-row.ttc{background:#f1f5f9;margin-top:4px;padding:12px 14px;border:none;font-size:16px;font-weight:800;}
  .legal-block{margin-top:28px;font-size:10px;color:#94a3b8;line-height:1.5;max-width:100%;}
  .inv-footer{margin-top:36px;text-align:center;font-size:11px;color:#94a3b8;}
  .preview-toolbar{position:fixed;bottom:0;left:0;right:0;z-index:9999;background:#fff;border-top:1px solid #e2e8f0;box-shadow:0 -4px 16px rgba(0,0,0,.08);padding:14px 20px;text-align:center;}
  .tb-btn{background:#3b6fb6;color:#fff;border:none;padding:10px 24px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;}
  .tb-btn:hover{background:#2d5a94;}
  @media print{
    body{padding:20px 24px 20px;}
    .no-print{display:none!important;}
  }
</style></head><body>
<div class="inv-top">
  ${logoBlock}
  <div class="inv-title-wrap">
    <h1 class="facture-title">Facture</h1>
    <div class="meta-block">
      <div><span class="meta-lbl">Numéro de facture :</span> <strong>${escapeHtml(entryHeader.invoice_number)}</strong></div>
      <div><span class="meta-lbl">Date d'émission :</span> <strong>${escapeHtml(String(dateFr))}</strong></div>
    </div>
  </div>
</div>
${rateNote}
<div class="addr-grid">
  <div class="issuer-col">
    <div class="issuer-name">${escapeHtml(INVOICE_ISSUER_NAME)}</div>
    <div class="issuer-lines">${issuerBody}</div>
  </div>
  <div class="client-col">
    ${beneficiaryHtml}
  </div>
</div>
<table class="inv-table">
  <thead>
    <tr>
      <th class="col-desc">Description</th>
      <th class="col-qty c">Quantité (h)</th>
      <th class="col-pu r">Prix unitaire HT</th>
      <th class="col-tva c">TVA (%)</th>
      <th class="col-ht r">Total HT</th>
    </tr>
  </thead>
  <tbody>${tableRows}</tbody>
</table>
<div class="sum-wrap">
  <div class="sum-box">
    <div class="sum-row"><span>Total HT</span><span>${formatMoneyPrint(totalHt, cur)}</span></div>
    <div class="sum-row"><span>Montant total de TVA</span><span>${formatMoneyPrint(totalVat, cur)}</span></div>
    <div class="sum-row ttc"><span>Total TTC</span><span>${formatMoneyPrint(totalTtc, cur)}</span></div>
  </div>
</div>
<p class="legal-block">${legalMentions}</p>
<p class="inv-footer">CG Developpement, SAS</p>
${toolbar}
</body></html>`;
}

/**
 * Rend la facture dans un iframe hors écran puis capture en canvas (html2canvas) — sans barre d’aperçu.
 */
function renderDevInvoiceToCanvas(entryHeader, lines, options) {
  const html = buildDevInvoicePreviewDocumentHtml(entryHeader, lines, options, false);
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'invoice-capture');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
      'position:fixed;left:-9999px;top:0;width:920px;min-height:2400px;height:auto;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    if (!doc) {
      document.body.removeChild(iframe);
      reject(new Error('iframe indisponible'));
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();

    const cleanup = () => {
      try {
        document.body.removeChild(iframe);
      } catch {
        /* ignore */
      }
    };

    const capture = async () => {
      try {
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(r, 500))));
        await Promise.all(
          [...doc.images].map(
            (img) =>
              img.complete
                ? Promise.resolve()
                : new Promise((res) => {
                    img.onload = () => res();
                    img.onerror = () => res();
                    setTimeout(res, 4000);
                  })
          )
        );
        const body = doc.body;
        if (!body) {
          cleanup();
          reject(new Error('Corps du document vide'));
          return;
        }
        const canvas = await html2canvas(body, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
          scrollX: 0,
          scrollY: 0,
          width: body.scrollWidth,
          height: body.scrollHeight,
        });
        cleanup();
        resolve(canvas);
      } catch (err) {
        cleanup();
        reject(err);
      }
    };

    let scheduled = false;
    const scheduleCapture = () => {
      if (scheduled) return;
      scheduled = true;
      setTimeout(capture, 80);
    };
    iframe.addEventListener('load', scheduleCapture);
    if (iframe.contentDocument?.readyState === 'complete') {
      scheduleCapture();
    }
    setTimeout(scheduleCapture, 1800);
  });
}

/** Découpe le canvas en bandes et assemble un PDF A4 portrait (marges 10 mm). */
function canvasToA4PdfBlob(canvas) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const contentW = pageW - 2 * margin;
  const contentH = pageH - 2 * margin;
  const slicePx = Math.max(1, Math.floor((contentH / contentW) * canvas.width));
  let yPx = 0;
  let first = true;
  while (yPx < canvas.height) {
    const hPx = Math.min(slicePx, canvas.height - yPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = hPx;
    const ctx = sliceCanvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(canvas, 0, yPx, canvas.width, hPx, 0, 0, canvas.width, hPx);
    const imgData = sliceCanvas.toDataURL('image/png');
    const sliceMmH = (hPx / canvas.width) * contentW;
    if (!first) pdf.addPage();
    first = false;
    pdf.addImage(imgData, 'PNG', margin, margin, contentW, sliceMmH, undefined, 'FAST');
    yPx += hPx;
  }
  return pdf.output('blob');
}

async function captureDevInvoiceToPdfBlob(entryHeader, lines, options) {
  const canvas = await renderDevInvoiceToCanvas(entryHeader, lines, options);
  return canvasToA4PdfBlob(canvas);
}

/**
 * Supprime l’ancienne PJ si besoin, génère un PDF A4 puis upload — retourne la nouvelle map fichiers par société.
 */
async function captureAndUploadDevInvoiceSnapshot(snap, filesByCompanyKey, token) {
  const ck = snap.companyKey;
  if (!ck) throw new Error('Société cible manquante');
  const { entryHeader, lines, options } = snap;
  const cur = options.currency || 'EUR';
  if (cur !== 'EUR') {
    const xr = parseFloat(options.exchangeRate);
    if (!xr || Number.isNaN(xr) || xr <= 0) {
      throw new Error('Taux de change invalide');
    }
  }
  let map = { ...filesByCompanyKey };
  const prevFileId = map[ck]?.file_id;
  if (prevFileId) {
    try {
      await axios.delete(`${API}/files/${prevFileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      console.error(e);
    }
    const { [ck]: _, ...rest } = map;
    map = rest;
  }
  const blob = await captureDevInvoiceToPdfBlob(entryHeader, lines, options);
  const maxSize = 10 * 1024 * 1024;
  if (blob.size > maxSize) {
    const err = new Error('PDF_TOO_LARGE');
    err.code = 'PDF_TOO_LARGE';
    throw err;
  }
  const safeNum = (entryHeader.invoice_number || 'facture').replace(/[^a-zA-Z0-9._-]+/g, '_');
  const uploadName = `Facture-${safeNum}.pdf`;
  const formData = new FormData();
  formData.append('file', blob, uploadName);
  const response = await axios.post(`${API}/files/upload`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return {
    ...map,
    [ck]: {
      file_id: response.data.id,
      file_name: response.data.original_name || uploadName,
    },
  };
}

/** Modals Développement : hauteur stable, corps scrollable (évite les sauts au changement d’options) */
const devModalShell =
  'flex flex-col gap-0 overflow-hidden p-0 w-[calc(100vw-1.25rem)] max-h-[90dvh] sm:max-h-[90vh] sm:rounded-lg';
const devModalHeader = 'shrink-0 space-y-1.5 px-5 pt-5 pb-3 sm:px-6 sm:pt-6 border-b border-border/60 text-left';
const devModalBody = 'flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4 sm:px-6';
const devModalFooter =
  'shrink-0 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2 px-5 py-4 sm:px-6 border-t border-border/60 bg-card';

/** Indicatif : créneaux × minutes par créneau → chaîne du type 2h30 */
const formatSlotsAsXxhXX = (slots, minutesPerSlot) => {
  const n = Math.max(0, parseInt(slots, 10) || 0);
  const totalMin = n * minutesPerSlot;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h${String(m).padStart(2, '0')}`;
};

const WORK_MONTH_NAMES_FR = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

/** Période de travail en fr-FR : jour précis ou mois entier ; repli sur date d’enregistrement si besoin. */
const displayWorkPeriodFr = (workDate, granularity, entryCreatedAtIso) => {
  const w = (workDate || '').trim();
  const gran = granularity === 'month' ? 'month' : 'day';
  if (/^\d{4}-\d{2}-\d{2}$/.test(w)) {
    if (gran === 'month') {
      const m = parseInt(w.slice(5, 7), 10);
      const y = parseInt(w.slice(0, 4), 10);
      if (m >= 1 && m <= 12 && y > 0) {
        return `${WORK_MONTH_NAMES_FR[m - 1]} ${y}`;
      }
    }
    return new Date(`${w}T12:00:00`).toLocaleDateString('fr-FR');
  }
  if (entryCreatedAtIso) {
    const d = new Date(entryCreatedAtIso);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('fr-FR');
  }
  return null;
};

const emptyLine = () => ({
  application_name: '',
  /** Plusieurs sociétés : une ligne journal/API est créée par société avec les mêmes créneaux / commentaire. */
  companies: [],
  work_date: todayISO(),
  work_date_granularity: 'day',
  creation_slots: 0,
  debug_slots: 0,
  comment: '',
});

/** Une ligne journal API (1 société) → format formulaire « Nouvelle entrée ». */
const journalApiLineToFormLine = (l) => {
  const snap = l.dev_rate_snapshot;
  const hasSnap =
    snap &&
    typeof snap.creation_hourly === 'number' &&
    !Number.isNaN(snap.creation_hourly) &&
    typeof snap.debug_hourly === 'number' &&
    !Number.isNaN(snap.debug_hourly);
  return {
    application_name: (l.application_name || '').trim(),
    work_date: (l.work_date || '').trim() || todayISO(),
    work_date_granularity: l.work_date_granularity === 'month' ? 'month' : 'day',
    creation_slots: parseInt(l.creation_slots, 10) || 0,
    debug_slots: parseInt(l.debug_slots, 10) || 0,
    comment: (l.comment || '').trim(),
    companies: [
      {
        company_id: (l.company_id != null ? String(l.company_id) : '').trim() || null,
        company_name: (l.company_name || '').trim(),
      },
    ],
    ...(hasSnap
      ? {
          dev_rate_snapshot: {
            creation_hourly: snap.creation_hourly,
            debug_hourly: snap.debug_hourly,
          },
        }
      : {}),
  };
};

const journalLineKey = (entryId, lineIndex) => `${entryId}:${lineIndex}`;

/**
 * Bouton +/− : un pas au clic, répétition au maintien (appui long).
 */
function DevSlotRepeatButton({ onStep, disabled, children, className }) {
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const stopHold = useCallback(() => {
    clearTimers();
    window.removeEventListener('pointerup', stopHold);
    window.removeEventListener('pointercancel', stopHold);
  }, [clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const onPointerDown = useCallback(
    (e) => {
      if (disabled || e.button !== 0) return;
      onStep();
      clearTimers();
      window.addEventListener('pointerup', stopHold);
      window.addEventListener('pointercancel', stopHold);
      timeoutRef.current = setTimeout(() => {
        intervalRef.current = setInterval(() => {
          onStep();
        }, 70);
      }, 450);
    },
    [disabled, onStep, clearTimers, stopHold]
  );

  return (
    <Button
      type="button"
      size="icon"
      variant="outline"
      className={className}
      disabled={disabled}
      onPointerDown={onPointerDown}
      onPointerUp={stopHold}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStep();
        }
      }}
    >
      {children}
    </Button>
  );
}

/**
 * Répartit un total entier (ex. créneaux) en `shareCount` parts dont la somme vaut exactement `total`.
 * Utilisé quand une saisie concerne plusieurs sociétés : le coût temps est divisé équitablement.
 */
function splitIntegerAcrossShares(total, shareCount) {
  const t = Math.max(0, Math.floor(Number(total) || 0));
  const n = Math.max(1, Math.floor(Number(shareCount) || 0));
  if (n <= 1) return [t];
  const base = Math.floor(t / n);
  const rem = t % n;
  return Array.from({ length: n }, (_, i) => base + (i < rem ? 1 : 0));
}

/** Aligné sur le backend `_dev_company_key` */
const devCompanyKey = (company_id, company_name) => {
  const cid = (company_id || '').trim();
  const cname = (company_name || '').trim();
  if (cid) return `id:${cid}`;
  return `name:${cname}`;
};

const DEV_CREATION_SLOT_HOURS = 0.5;
const DEV_DEBUG_SLOT_HOURS = 0.25;

const lineCreationHours = (line) =>
  (parseInt(line.creation_slots, 10) || 0) * DEV_CREATION_SLOT_HOURS;
const lineDebugHours = (line) => (parseInt(line.debug_slots, 10) || 0) * DEV_DEBUG_SLOT_HOURS;

/** Tarifs €/h effectifs : instantané ligne (`dev_rate_snapshot`) si présent, sinon grille Paramètres. */
function lineEffectiveHourlyRates(line, invoiceRatesMap) {
  const snap = line.dev_rate_snapshot;
  if (
    snap &&
    typeof snap.creation_hourly === 'number' &&
    !Number.isNaN(snap.creation_hourly) &&
    typeof snap.debug_hourly === 'number' &&
    !Number.isNaN(snap.debug_hourly)
  ) {
    return { creation_hourly: snap.creation_hourly, debug_hourly: snap.debug_hourly };
  }
  const ck = devCompanyKey(line.company_id, line.company_name);
  const r = invoiceRatesMap[ck];
  if (!r || Number.isNaN(r.creation_hourly) || Number.isNaN(r.debug_hourly)) return null;
  return { creation_hourly: r.creation_hourly, debug_hourly: r.debug_hourly };
}

function lineTimeEuroFromMaps(line, invoiceRatesMap) {
  const rates = lineEffectiveHourlyRates(line, invoiceRatesMap);
  if (!rates) return null;
  const ch = lineCreationHours(line);
  const dh = lineDebugHours(line);
  return Math.round((ch * rates.creation_hourly + dh * rates.debug_hourly) * 100) / 100;
}

/** Total € journal pour les lignes affichées d’une entrée (même logique que la facturation / dashboard). */
function journalEntryLinesTotalEuro(lines, invoiceRatesMap) {
  let sum = 0;
  let hasValued = false;
  let hasUnpricedWork = false;
  for (const l of lines || []) {
    const slotsC = parseInt(l.creation_slots, 10) || 0;
    const slotsD = parseInt(l.debug_slots, 10) || 0;
    const hasWork = slotsC > 0 || slotsD > 0;
    const t = lineTimeEuroFromMaps(l, invoiceRatesMap);
    if (t !== null) {
      sum += t;
      hasValued = true;
    } else if (hasWork) {
      hasUnpricedWork = true;
    }
  }
  return {
    sum: Math.round(sum * 100) / 100,
    hasValued,
    hasUnpricedWork,
  };
}

/**
 * Sous-total bloc : somme des temps € (lignes cochées) + forfait MEP si coché et au moins une ligne retenue.
 * invoiceLineIncluded[k] === false exclut la ligne ; absent ou true = incluse.
 */
function bundleDetailSubtotalFromMaps(g, bundleMepUi, invoiceLineIncluded, invoiceRatesMap) {
  let timeSum = 0;
  let anyTime = false;
  let anyLinePicked = false;
  for (const l of g.lines) {
    const k = journalLineKey(l.entry_id, l.line_index);
    if (invoiceLineIncluded[k] === false) continue;
    anyLinePicked = true;
    const t = lineTimeEuroFromMaps(l, invoiceRatesMap);
    if (t !== null) {
      timeSum += t;
      anyTime = true;
    }
  }
  const bu = bundleMepUi[g.key] || {};
  const raw = (bu.production || '').trim();
  const prod = raw === '' ? 0 : parseFloat(raw.replace(',', '.'));
  const p = Number.isNaN(prod) || prod < 0 ? 0 : prod;
  const prodPart = bu.mep && anyLinePicked ? p : 0;
  if (!anyTime && prodPart === 0) return null;
  return Math.round((timeSum + prodPart) * 100) / 100;
}

/** Valeur stable pour le combobox société (id ou préfixe name:) */
const companyComboboxValue = (c) => {
  if (c.id) return String(c.id);
  return `name:${(c.name || '').trim()}`;
};

/** Filtre journal : même convention que `devCompanyKey` (`id:…` / `name:…`) ; rétrocompat id seul. */
const lineMatchesJournalFilters = (line, filterCompanyKey, filterApplicationName) => {
  const fa = (filterApplicationName || '').trim();
  if (fa) {
    const an = (line.application_name || '').trim();
    if (an !== fa) return false;
  }
  const fc = (filterCompanyKey || '').trim();
  if (!fc) return true;
  const cid = line.company_id != null ? String(line.company_id).trim() : '';
  const cname = (line.company_name || '').trim();
  const lineKey = devCompanyKey(cid, cname);
  if (fc === lineKey) return true;
  if (cid && fc === cid) return true;
  if (fc.startsWith('id:')) {
    return cid === fc.slice(3).trim();
  }
  if (fc.startsWith('name:')) {
    return cname.trim() === fc.slice(5).trim();
  }
  return false;
};

export const Development = () => {
  const { token, user, isAdmin, canAccess: canAccessModule, canWrite } = useAuth();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }),
    [token]
  );

  const [applications, setApplications] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [newOpen, setNewOpen] = useState(false);
  /** Si défini, le dialogue journal est en mode édition (PUT) pour cette entrée. */
  const [editingDevEntryId, setEditingDevEntryId] = useState(null);
  const [lines, setLines] = useState([emptyLine()]);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rateRows, setRateRows] = useState([]);
  const [applyNewRatesToExistingLines, setApplyNewRatesToExistingLines] = useState(false);

  const [invoiceOpen, setInvoiceOpen] = useState(false);
  /** True pendant le premier chargement des données du modal Facturer (aperçu immédiat du cadre). */
  const [invoiceBootstrapLoading, setInvoiceBootstrapLoading] = useState(false);
  /** Lignes de journal non facturées (GET /dev/billing/unbilled-lines) */
  const [unbilledLines, setUnbilledLines] = useState([]);
  /** Par clé société : mise en production + forfait production (€) pour toutes les apps de la société */
  const [bundleMepUi, setBundleMepUi] = useState({});
  /** Tarifs €/h par clé société (GET /dev/settings à l’ouverture du modal Facturer) */
  const [invoiceRatesMap, setInvoiceRatesMap] = useState({});
  /** Synthèse page : montants non facturé / facturé par société + application (GET /dev/billing/company-dashboard) */
  const [companyDashboard, setCompanyDashboard] = useState([]);
  /** Par société : panneau détail (applications / lignes journal) ouvert */
  const [expandedBundleKeys, setExpandedBundleKeys] = useState({});
  /** Inclure la société dans la facture (détail : lignes journal une par une) */
  const [groupIncluded, setGroupIncluded] = useState({});
  /** Par journalLineKey : false = exclure cette ligne du lot ; absent = incluse */
  const [invoiceLineIncluded, setInvoiceLineIncluded] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [overrideRates, setOverrideRates] = useState({});
  /** Données pour facture type « entrée produit » (onglet Facture) */
  const [entryInvoiceData, setEntryInvoiceData] = useState({
    invoice_number: '',
    supplier_id: '',
    supplier_name: '',
    payer_company_id: '',
    payer_company_name: '',
    invoice_date: todayISO(),
    file_id: null,
    file_name: null,
    currency: 'EUR',
    exchange_rate: 1.0,
  });
  const [devBillingRefs, setDevBillingRefs] = useState({
    suppliers: [],
    sectors: [],
    products: [],
    categories: [],
  });
  /** Assistant Facturer : 1 = lignes, 2 = informations facture, 3 = récapitulatif */
  const [invoiceWizardStep, setInvoiceWizardStep] = useState(1);
  const [devInvoiceAttachLoading, setDevInvoiceAttachLoading] = useState(false);
  const [devInvoiceBatchLoading, setDevInvoiceBatchLoading] = useState(false);
  const [devInvoiceBatchProgress, setDevInvoiceBatchProgress] = useState(null);
  /** Aperçu facture (modal) : snapshot + allowAttach false après enregistrement définitif. */
  const [devInvoicePreviewPayload, setDevInvoicePreviewPayload] = useState(null);
  /** Société dont l’aperçu PDF / la PJ sont édités (une facture = une société cliente). */
  const [invoicePreviewCompanyKey, setInvoicePreviewCompanyKey] = useState('');
  const [invoiceFilesByCompanyKey, setInvoiceFilesByCompanyKey] = useState({});
  /** Numéros FA-AAAAMM-DEV-NNNN réservés à l’étape 2 (un par société facturée). */
  const [invoiceNumbersReserved, setInvoiceNumbersReserved] = useState([]);
  const [invoiceNumbersLoading, setInvoiceNumbersLoading] = useState(false);
  const [devInvoiceExchangeLoading, setDevInvoiceExchangeLoading] = useState(false);
  const [devInvoiceExchangeInfo, setDevInvoiceExchangeInfo] = useState(null);
  /** Filtres journal (côté API sur tout le journal, pagination = résultats filtrés) */
  const [journalFilterCompany, setJournalFilterCompany] = useState('');
  const [journalFilterApplication, setJournalFilterApplication] = useState('');
  /** Apps / sociétés présentes dans le journal (toutes pages) — GET /dev/journal/filter-options */
  const [journalFilterLineMeta, setJournalFilterLineMeta] = useState({
    application_names: [],
    companies: [],
  });
  const [devEntryDeleteTarget, setDevEntryDeleteTarget] = useState(null);
  const [devEntryDeleteLoading, setDevEntryDeleteLoading] = useState(false);

  const canAccess = canAccessModule('development');
  const canEditDev = canWrite('development');

  const loadRefs = useCallback(async () => {
    try {
      const [appsRes, compRes] = await Promise.all([
        axios.get(`${API}/external/applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/external/companies`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setApplications(Array.isArray(appsRes.data) ? appsRes.data : []);
      setCompanies(Array.isArray(compRes.data) ? compRes.data : []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur chargement applications / sociétés');
    }
  }, [token]);

  const confirmDeleteDevEntry = async () => {
    if (!devEntryDeleteTarget?.id) return;
    setDevEntryDeleteLoading(true);
    try {
      await axios.delete(`${API}/dev/entries/${devEntryDeleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Entrée développement supprimée');
      setDevEntryDeleteTarget(null);
      loadEntries();
      loadJournalFilterOptions();
      fetchUnbilledAndRates().catch(() => {});
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Suppression impossible');
    } finally {
      setDevEntryDeleteLoading(false);
    }
  };

  const loadJournalFilterOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dev/journal/filter-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJournalFilterLineMeta({
        application_names: res.data.application_names || [],
        companies: res.data.companies || [],
      });
    } catch (e) {
      console.error(e);
    }
  }, [token]);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      const fa = (journalFilterApplication || '').trim();
      if (fa) params.journal_application = fa;
      const fc = (journalFilterCompany || '').trim();
      if (fc) params.journal_company_key = fc;
      const res = await axios.get(`${API}/dev/entries`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setEntries(res.data.items || []);
      setTotal(res.data.total ?? 0);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Accès refusé à l’espace Développement');
      } else {
        toast.error('Erreur chargement des entrées');
      }
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, journalFilterApplication, journalFilterCompany]);

  useEffect(() => {
    if (canAccess) {
      loadRefs();
      loadJournalFilterOptions();
    }
  }, [canAccess, loadRefs, loadJournalFilterOptions]);

  useEffect(() => {
    if (canAccess) {
      loadEntries();
    }
  }, [canAccess, loadEntries]);

  const openSettings = async () => {
    try {
      const [settingsRes, compRes] = await Promise.all([
        axios.get(`${API}/dev/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/external/companies`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const rates = settingsRes.data.rates || [];
      const comps = Array.isArray(compRes.data) ? compRes.data : [];
      const rows = comps.map((c) => {
        const match =
          rates.find((r) => r.company_id && r.company_id === c.id) ||
          rates.find((r) => !r.company_id && r.company_name === c.name);
        return {
          company_id: c.id || '',
          company_name: c.name || '',
          creation_hourly: match?.creation_hourly ?? 0,
          debug_hourly: match?.debug_hourly ?? 0,
        };
      });
      setRateRows(rows);
      setApplyNewRatesToExistingLines(false);
      setSettingsOpen(true);
    } catch (e) {
      toast.error('Impossible de charger les paramètres');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/dev/settings`,
        {
          rates: rateRows,
          apply_new_rates_to_existing_lines: applyNewRatesToExistingLines,
        },
        { headers }
      );
      toast.success('Paramètres enregistrés');
      setSettingsOpen(false);
      loadEntries();
      fetchUnbilledAndRates().catch(() => {});
    } catch (e) {
      toast.error('Erreur enregistrement des paramètres');
    } finally {
      setSaving(false);
    }
  };

  const patchBundleMepUi = (bundleKey, patch) => {
    setBundleMepUi((prev) => ({
      ...prev,
      [bundleKey]: {
        mep: false,
        production: '',
        ...prev[bundleKey],
        ...patch,
      },
    }));
  };

  const fetchUnbilledAndRates = useCallback(async () => {
    const [linesRes, settingsRes, dashRes] = await Promise.all([
      axios.get(`${API}/dev/billing/unbilled-lines`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${API}/dev/settings`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/dev/billing/company-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    setUnbilledLines(linesRes.data.lines || []);
    setCompanyDashboard(dashRes.data.companies || []);
    const rates = settingsRes.data.rates || [];
    const rm = {};
    rates.forEach((r) => {
      const k = r.company_id ? `id:${r.company_id}` : `name:${(r.company_name || '').trim()}`;
      rm[k] = {
        creation_hourly: Number(r.creation_hourly),
        debug_hourly: Number(r.debug_hourly),
      };
    });
    setInvoiceRatesMap(rm);
  }, [token]);

  useEffect(() => {
    if (!canAccess) return undefined;
    fetchUnbilledAndRates().catch(() => {});
    return undefined;
  }, [canAccess, fetchUnbilledAndRates]);

  const openInvoice = () => {
    setPreview(null);
    setBundleMepUi({});
    setInvoiceLineIncluded({});
    setExpandedBundleKeys({});
    setGroupIncluded({});
    setOverrideRates({});
    setInvoiceWizardStep(1);
    setInvoicePreviewCompanyKey('');
    setInvoiceFilesByCompanyKey({});
    setInvoiceNumbersReserved([]);
    setInvoiceNumbersLoading(false);
    setDevInvoiceBatchLoading(false);
    setDevInvoiceBatchProgress(null);
    setDevBillingRefs({ suppliers: [], sectors: [], products: [], categories: [] });
    setInvoiceOpen(true);
    setInvoiceBootstrapLoading(true);

    const catalogPromise = Promise.all([
      axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/categories`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);

    (async () => {
      try {
        const [, supRes, secRes, compRes] = await Promise.all([
          fetchUnbilledAndRates(),
          axios.get(`${API}/external/suppliers`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/external/secteurs`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/external/companies`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const suppliers = Array.isArray(supRes.data) ? supRes.data : [];
        const allCompanies = Array.isArray(compRes.data) ? compRes.data : [];
        setCompanies(allCompanies);
        const cg = suppliers.find((s) => /cg\s*d[ée]v/i.test((s.name || '').trim()));
        const cgPayer = allCompanies.find((c) =>
          /^cg\s*developpement$/i.test((c.name || '').replace(/\s+/g, ' ').trim())
        );
        setDevBillingRefs((prev) => ({
          ...prev,
          suppliers,
          sectors: Array.isArray(secRes.data) ? secRes.data : [],
        }));
        setEntryInvoiceData({
          invoice_number: '',
          supplier_id: cg?.id || '',
          supplier_name: cg?.name || 'CG Développement',
          payer_company_id: cgPayer?.id || '',
          payer_company_name: cgPayer?.name || 'CG DEVELOPPEMENT',
          invoice_date: new Date().toISOString().slice(0, 10),
          file_id: null,
          file_name: null,
          currency: 'EUR',
          exchange_rate: 1.0,
        });
        setDevInvoiceExchangeInfo(null);
      } catch (e) {
        toast.error('Impossible de charger les données de facturation');
        setInvoiceOpen(false);
        setInvoiceBootstrapLoading(false);
        return;
      }

      setInvoiceBootstrapLoading(false);

      try {
        const [prodRes, catRes] = await catalogPromise;
        setDevBillingRefs((prev) => ({
          ...prev,
          products: Array.isArray(prodRes.data) ? prodRes.data : [],
          categories: Array.isArray(catRes.data) ? catRes.data : [],
        }));
      } catch (e) {
        console.error(e);
        toast.error('Impossible de charger le catalogue produits / catégories');
      }
    })();
  };

  const submitNewEntry = async () => {
    const payloadLines = [];
    for (const l of lines) {
      const app = (l.application_name || '').trim();
      if (!app) continue;
      const comps = l.companies || [];
      if (comps.length === 0) continue;
      const gran = l.work_date_granularity === 'month' ? 'month' : 'day';
      let workDate = (l.work_date || todayISO()).trim();
      if (gran === 'month') {
        if (/^\d{4}-\d{2}$/.test(workDate)) workDate = `${workDate}-01`;
        else if (/^\d{4}-\d{2}-\d{2}$/.test(workDate)) workDate = `${workDate.slice(0, 7)}-01`;
      }
      const creationSlots = Math.max(0, parseInt(l.creation_slots, 10) || 0);
      const debugSlots = Math.max(0, parseInt(l.debug_slots, 10) || 0);
      const comment = (l.comment || '').trim();
      const validComps = comps.filter((c) => (c.company_name || '').trim());
      const n = validComps.length;
      const creationParts = splitIntegerAcrossShares(creationSlots, n);
      const debugParts = splitIntegerAcrossShares(debugSlots, n);
      const snap = l.dev_rate_snapshot;
      const hasSnap =
        snap &&
        typeof snap.creation_hourly === 'number' &&
        !Number.isNaN(snap.creation_hourly) &&
        typeof snap.debug_hourly === 'number' &&
        !Number.isNaN(snap.debug_hourly);
      validComps.forEach((c, i) => {
        const cname = (c.company_name || '').trim();
        payloadLines.push({
          application_name: app,
          company_id: (c.company_id || '').trim() || null,
          company_name: cname,
          work_date: workDate,
          work_date_granularity: gran,
          creation_slots: creationParts[i],
          debug_slots: debugParts[i],
          comment,
          ...(hasSnap
            ? {
                dev_rate_snapshot: {
                  creation_hourly: snap.creation_hourly,
                  debug_hourly: snap.debug_hourly,
                },
              }
            : {}),
        });
      });
    }
    if (payloadLines.length === 0) {
      toast.error('Ajoutez au moins une ligne complète (application + au moins une société)');
      return;
    }
    setSaving(true);
    try {
      if (editingDevEntryId) {
        await axios.put(
          `${API}/dev/entries/${editingDevEntryId}`,
          { lines: payloadLines },
          { headers }
        );
        toast.success('Entrée mise à jour');
      } else {
        await axios.post(`${API}/dev/entries`, { lines: payloadLines }, { headers });
        toast.success('Entrée enregistrée');
      }
      setNewOpen(false);
      setEditingDevEntryId(null);
      setLines([emptyLine()]);
      loadEntries();
      loadJournalFilterOptions();
      fetchUnbilledAndRates().catch(() => {});
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur à l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const openEditDevEntry = (row) => {
    const ls = row.lines || [];
    if (!ls.length || ls.some((l) => l.billed)) return;
    setEditingDevEntryId(row.id);
    setLines(ls.map(journalApiLineToFormLine));
    setNewOpen(true);
  };

  const updateLine = (index, patch) => {
    setLines((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const bumpLineSlot = useCallback((lineIndex, field, delta) => {
    setLines((prev) => {
      const next = [...prev];
      const cur = next[lineIndex];
      if (!cur) return prev;
      const raw = (parseInt(cur[field], 10) || 0) + delta;
      next[lineIndex] = { ...cur, [field]: Math.max(0, raw) };
      return next;
    });
  }, []);

  const companyById = useMemo(() => {
    const m = {};
    companies.forEach((c) => {
      if (c.id) m[c.id] = c;
    });
    return m;
  }, [companies]);

  const addCompanyToLine = (idx, comboboxValue) => {
    if (!comboboxValue) return;
    const c =
      companyById[comboboxValue] || companies.find((co) => companyComboboxValue(co) === comboboxValue);
    if (!c) return;
    const entry = { company_id: c.id || '', company_name: (c.name || '').trim() };
    const k = devCompanyKey(entry.company_id, entry.company_name);
    setLines((prev) => {
      const line = prev[idx];
      if (!line) return prev;
      const list = [...(line.companies || [])];
      if (list.some((x) => devCompanyKey(x.company_id, x.company_name) === k)) return prev;
      list.push(entry);
      const next = [...prev];
      next[idx] = { ...line, companies: list };
      return next;
    });
  };

  const removeCompanyFromLine = (idx, companyKey) => {
    setLines((prev) => {
      const line = prev[idx];
      if (!line) return prev;
      const next = [...prev];
      next[idx] = {
        ...line,
        companies: (line.companies || []).filter(
          (x) => devCompanyKey(x.company_id, x.company_name) !== companyKey
        ),
      };
      return next;
    });
  };

  const applicationComboboxOptions = useMemo(
    () => [
      { value: '', label: '— Aucune sélection' },
      ...applications
        .map((a) => ({ value: (a.name || '').trim(), label: a.name || '' }))
        .filter((o) => o.value),
    ],
    [applications]
  );

  const companyComboboxOptions = useMemo(
    () => [
      { value: '', label: '— Aucune sélection' },
      ...companies.map((c) => ({
        value: companyComboboxValue(c),
        label: (c.name || '').trim() || companyComboboxValue(c),
      })),
    ],
    [companies]
  );

  /** Liste sans entrée vide — pour « Ajouter une société » (placeholder visible). */
  const companyComboboxAddOptions = useMemo(
    () =>
      companies
        .map((c) => ({
          value: companyComboboxValue(c),
          label: (c.name || '').trim() || companyComboboxValue(c),
        }))
        .filter((o) => o.value),
    [companies]
  );

  const journalCompanyFilterOptions = useMemo(() => {
    const seen = new Set();
    const rest = [];
    const pushKey = (company_id, company_name) => {
      const cid = (company_id != null ? String(company_id) : '').trim();
      const cname = (company_name || '').trim();
      if (!cid && !cname) return;
      const k = devCompanyKey(cid, cname);
      if (!k || k === 'name:' || seen.has(k)) return;
      seen.add(k);
      const label = cname || cid || k;
      rest.push({ value: k, label });
    };
    companies.forEach((c) => {
      pushKey(c.id, (c.name || '').trim());
    });
    (journalFilterLineMeta.companies || []).forEach((c) => {
      pushKey(c.company_id, (c.company_name || '').trim());
    });
    rest.sort((a, b) => a.label.localeCompare(b.label, 'fr'));
    return [{ value: '', label: 'Toutes les sociétés' }, ...rest];
  }, [companies, journalFilterLineMeta.companies]);

  const journalApplicationFilterOptions = useMemo(() => {
    const names = new Set();
    applications.forEach((a) => {
      const n = (a.name || '').trim();
      if (n) names.add(n);
    });
    (journalFilterLineMeta.application_names || []).forEach((n) => {
      const t = (n || '').trim();
      if (t) names.add(t);
    });
    const opts = Array.from(names).sort((a, b) => a.localeCompare(b, 'fr'));
    return [{ value: '', label: 'Toutes les applications' }, ...opts.map((n) => ({ value: n, label: n }))];
  }, [applications, journalFilterLineMeta.application_names]);

  const journalFilteredEntries = useMemo(() => {
    const fc = journalFilterCompany;
    const fa = journalFilterApplication;
    const hasFilter = !!(fc || fa);
    if (!hasFilter) {
      return entries.map((row) => ({ row, displayLines: row.lines || [] }));
    }
    const out = [];
    for (const row of entries) {
      const ls = row.lines || [];
      const displayLines = ls.filter((l) => lineMatchesJournalFilters(l, fc, fa));
      if (displayLines.length === 0) continue;
      out.push({ row, displayLines });
    }
    return out;
  }, [entries, journalFilterCompany, journalFilterApplication]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const unbilledCompanyBundles = useMemo(() => {
    const m = new Map();
    for (const l of unbilledLines) {
      const key = devCompanyKey(l.company_id, l.company_name);
      if (!m.has(key)) {
        m.set(key, {
          key,
          company_id: l.company_id || '',
          company_name: (l.company_name || '').trim() || '—',
          lines: [],
          totalCreationSlots: 0,
          totalDebugSlots: 0,
        });
      }
      const g = m.get(key);
      g.lines.push(l);
      g.totalCreationSlots += parseInt(l.creation_slots, 10) || 0;
      g.totalDebugSlots += parseInt(l.debug_slots, 10) || 0;
    }
    return Array.from(m.values()).sort((a, b) => a.company_name.localeCompare(b.company_name, 'fr'));
  }, [unbilledLines]);

  const billingCompanyKeysOrdered = useMemo(() => {
    const keys = [];
    for (const g of unbilledCompanyBundles) {
      if (!groupIncluded[g.key]) continue;
      const anyLine = g.lines.some(
        (l) => invoiceLineIncluded[journalLineKey(l.entry_id, l.line_index)] !== false
      );
      if (anyLine) keys.push(g.key);
    }
    keys.sort((a, b) => a.localeCompare(b));
    return keys;
  }, [unbilledCompanyBundles, groupIncluded, invoiceLineIncluded]);

  useEffect(() => {
    if (!invoiceOpen) return undefined;
    const keys = billingCompanyKeysOrdered;
    if (keys.length === 0) return undefined;
    setInvoicePreviewCompanyKey((prev) => (prev && keys.includes(prev) ? prev : keys[0]));
    return undefined;
  }, [invoiceOpen, billingCompanyKeysOrdered]);

  const invoiceLineItemsPayload = useMemo(() => {
    const items = [];
    for (const g of unbilledCompanyBundles) {
      if (!groupIncluded[g.key]) continue;
      const bu = bundleMepUi[g.key] || {};
      const mep = !!bu.mep;
      const raw = (bu.production || '').trim();
      const prod = raw === '' ? 0 : parseFloat(raw.replace(',', '.'));
      const prodAmount = mep && !Number.isNaN(prod) && prod >= 0 ? prod : 0;
      let firstIncluded = true;
      for (const l of g.lines) {
        const lk = journalLineKey(l.entry_id, l.line_index);
        if (invoiceLineIncluded[lk] === false) continue;
        items.push({
          entry_id: l.entry_id,
          line_index: l.line_index,
          mise_en_production: mep,
          production_amount: mep && firstIncluded ? prodAmount : 0,
        });
        firstIncluded = false;
      }
    }
    return items;
  }, [unbilledCompanyBundles, groupIncluded, bundleMepUi, invoiceLineIncluded]);

  const invoicePayloadDeps = useMemo(
    () => JSON.stringify(invoiceLineItemsPayload),
    [invoiceLineItemsPayload]
  );

  const lineTimeEuro = (line) => lineTimeEuroFromMaps(line, invoiceRatesMap);

  const bundleEstimateTimeOnly = (g) => {
    let sum = 0;
    for (const l of g.lines) {
      const lk = journalLineKey(l.entry_id, l.line_index);
      if (groupIncluded[g.key] && invoiceLineIncluded[lk] === false) continue;
      const t = lineTimeEuroFromMaps(l, invoiceRatesMap);
      if (t === null) return null;
      sum += t;
    }
    return Math.round(sum * 100) / 100;
  };

  const bundleDetailSubtotal = (g) =>
    bundleDetailSubtotalFromMaps(g, bundleMepUi, invoiceLineIncluded, invoiceRatesMap);

  const toggleBundleExpand = (bundleKey) => {
    setExpandedBundleKeys((prev) => ({ ...prev, [bundleKey]: !prev[bundleKey] }));
  };

  useEffect(() => {
    if (!invoiceOpen) {
      setPreviewLoading(false);
      return undefined;
    }
    if (!invoiceLineItemsPayload.length) {
      setPreview(null);
      setOverrideRates({});
      setPreviewLoading(false);
      return undefined;
    }
    const ac = new AbortController();
    const timer = setTimeout(() => {
      setPreviewLoading(true);
      axios
        .post(
          `${API}/dev/billing/preview`,
          { line_items: invoiceLineItemsPayload },
          { headers, signal: ac.signal }
        )
        .then((res) => {
          setPreview(res.data);
          const next = {};
          (res.data.companies || []).forEach((c) => {
            if (c.dev?.missing_rates || c.mep?.missing_rates) {
              next[c.company_key] = { creation: '', debug: '' };
            }
          });
          setOverrideRates(next);
          if (!res.data.ready) {
            toast.message('Tarifs manquants : complétez les €/h ci-dessous puis confirmez.');
          }
        })
        .catch((e) => {
          if (e.code === 'ERR_CANCELED' || ac.signal.aborted) return;
          const d = e.response?.data?.detail;
          toast.error(typeof d === 'string' ? d : 'Erreur aperçu facturation');
          setPreview(null);
        })
        .finally(() => {
          if (!ac.signal.aborted) {
            setPreviewLoading(false);
          }
        });
    }, 400);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [invoiceOpen, invoicePayloadDeps, headers]);

  const invoiceCanConfirm =
    preview &&
    invoiceLineItemsPayload.length > 0 &&
    (preview.ready ||
      (preview.companies || []).every((c) => {
        const devNeed = c.dev?.missing_rates;
        const mepNeed = c.mep?.missing_rates;
        if (!devNeed && !mepNeed) return true;
        const o = overrideRates[c.company_key];
        if (!o) return false;
        const ch = parseFloat(o.creation);
        const dh = parseFloat(o.debug);
        return !Number.isNaN(ch) && !Number.isNaN(dh) && ch >= 0 && dh >= 0;
      }));

  const effectiveInvoiceRatesMap = useMemo(() => {
    const m = { ...invoiceRatesMap };
    for (const ck of Object.keys(overrideRates)) {
      const o = overrideRates[ck];
      const ch = parseFloat(o?.creation);
      const dh = parseFloat(o?.debug);
      if (!Number.isNaN(ch) && !Number.isNaN(dh) && ch >= 0 && dh >= 0) {
        m[ck] = { creation_hourly: ch, debug_hourly: dh };
      }
    }
    return m;
  }, [invoiceRatesMap, overrideRates]);

  const devInvoiceProduct = useMemo(() => {
    const products = devBillingRefs.products || [];
    const categories = devBillingRefs.categories || [];
    const catNorm = DEV_INVOICE_CATEGORY_NAME.replace(/\s+/g, ' ').trim().toLowerCase();
    const cat = categories.find(
      (c) => (c.name || '').replace(/\s+/g, ' ').trim().toLowerCase() === catNorm
    );
    return (
      products.find(
        (p) =>
          (p.name || '').trim() === DEV_INVOICE_PRODUCT_NAME && (!cat || p.category_id === cat.id)
      ) || products.find((p) => (p.name || '').trim() === DEV_INVOICE_PRODUCT_NAME) ||
      null
    );
  }, [devBillingRefs.products, devBillingRefs.categories]);

  const proposedEntryLines = useMemo(() => {
    if (!devInvoiceProduct?.id) return [];
    const sectors = devBillingRefs.sectors || [];
    const categoriesList = devBillingRefs.categories || [];
    const categoryObj = categoriesList.find((c) => c.id === devInvoiceProduct.category_id);
    const categoryName = categoryObj?.name || DEV_INVOICE_CATEGORY_NAME;
    const vat = devInvoiceProduct.default_vat_rate ?? 20;
    const drafts = [];
    for (const g of unbilledCompanyBundles) {
      if (!groupIncluded[g.key]) continue;
      const bu = bundleMepUi[g.key] || {};
      const mep = !!bu.mep;
      const raw = (bu.production || '').trim();
      const prodAmount = mep && raw !== '' ? Math.max(0, parseFloat(raw.replace(',', '.')) || 0) : 0;
      let firstIncluded = true;
      for (const l of g.lines) {
        const lk = journalLineKey(l.entry_id, l.line_index);
        if (invoiceLineIncluded[lk] === false) continue;
        const ck = devCompanyKey(l.company_id, l.company_name);
        const effRates = lineEffectiveHourlyRates(l, effectiveInvoiceRatesMap);
        const chH = lineCreationHours(l);
        const dhH = lineDebugHours(l);
        const tEuro = lineTimeEuroFromMaps(l, effectiveInvoiceRatesMap);
        const timeHt = tEuro ?? 0;
        let mepHt = 0;
        if (mep && firstIncluded && prodAmount > 0) {
          mepHt = Math.round(prodAmount * 100) / 100;
        }
        firstIncluded = false;
        const ht = Math.round((timeHt + mepHt) * 100) / 100;
        const companyObj = matchCompanyForJournalLine(l, companies);
        const { establishment_id: estId, establishment_name: estName } = pickSiegeEstablishment(companyObj);
        const companyIdForSector = companyObj?.id || l.company_id || '';
        const { sector_id: secId, sector_name: secName } = pickDirectionSector(sectors, estId, companyIdForSector);
        const roundedHt = ht;
        const am = calculateEntryLineAmounts(roundedHt, vat);
        drafts.push({
          id: `dev-entry-${lk}`,
          journalKey: lk,
          application_name: (l.application_name || '').trim(),
          company_key: ck,
          dev_creation_hours: chH,
          dev_debug_hours: dhH,
          dev_creation_rate_eur: effRates?.creation_hourly ?? null,
          dev_debug_rate_eur: effRates?.debug_hourly ?? null,
          dev_mep_ht_eur: mepHt,
          category_id: devInvoiceProduct.category_id || '',
          category_name: categoryName,
          product_id: devInvoiceProduct.id,
          product_name: devInvoiceProduct.name,
          entry_type: 'one_off',
          billing_cycle: 'monthly',
          company_id: companyObj?.id || l.company_id || '',
          company_name: companyObj?.name || l.company_name || '',
          establishment_id: estId,
          establishment_name: estName,
          sector_id: secId,
          sector_name: secName,
          user_id: '',
          user_name: '',
          amount_ht: roundedHt,
          vat_rate: vat,
          amount_vat: am.amount_vat,
          amount_ttc: am.amount_ttc,
        });
      }
    }
    for (const row of drafts) {
      const am = calculateEntryLineAmounts(row.amount_ht, row.vat_rate);
      row.amount_vat = am.amount_vat;
      row.amount_ttc = am.amount_ttc;
    }
    return drafts;
  }, [
    devInvoiceProduct,
    unbilledCompanyBundles,
    groupIncluded,
    bundleMepUi,
    invoiceLineIncluded,
    effectiveInvoiceRatesMap,
    companies,
    devBillingRefs.sectors,
    devBillingRefs.categories,
  ]);

  const proposedLineByJournalKey = useMemo(() => {
    const m = new Map();
    for (const row of proposedEntryLines) {
      m.set(row.journalKey, row);
    }
    return m;
  }, [proposedEntryLines]);

  const companyNameByBillingKey = useMemo(() => {
    const m = {};
    for (const g of unbilledCompanyBundles) {
      m[g.key] = g.company_name || '—';
    }
    return m;
  }, [unbilledCompanyBundles]);

  const invoiceWizardDisplay = useMemo(() => {
    const cur = entryInvoiceData.currency || 'EUR';
    const xr = parseFloat(entryInvoiceData.exchange_rate) || 1;
    const lines = proposedEntryLines.map((l) => {
      const { ht, vat, ttc } = convertDevLineAmountsFromEur(l, cur === 'USD' ? 'USD' : 'EUR', xr);
      return { ...l, d_ht: ht, d_vat: vat, d_ttc: ttc };
    });
    const totals = lines.reduce(
      (acc, l) => ({
        ht: acc.ht + l.d_ht,
        vat: acc.vat + l.d_vat,
        ttc: acc.ttc + l.d_ttc,
      }),
      { ht: 0, vat: 0, ttc: 0 }
    );
    return { lines, totals, currency: cur, rate: xr };
  }, [proposedEntryLines, entryInvoiceData.currency, entryInvoiceData.exchange_rate]);

  const devInvoicePreviewHtml = useMemo(() => {
    if (!devInvoicePreviewPayload) return '';
    const { entryHeader, lines, options } = devInvoicePreviewPayload;
    return buildDevInvoicePreviewDocumentHtml(entryHeader, lines, options, false);
  }, [devInvoicePreviewPayload]);

  const devInvoiceHeaderOk =
    !!(entryInvoiceData.invoice_number || '').trim() &&
    !!(entryInvoiceData.supplier_name || '').trim() &&
    !!(entryInvoiceData.payer_company_id || '').trim() &&
    !!(entryInvoiceData.invoice_date || '').trim() &&
    (entryInvoiceData.currency === 'EUR' ||
      (parseFloat(entryInvoiceData.exchange_rate) > 0 &&
        !Number.isNaN(parseFloat(entryInvoiceData.exchange_rate))));

  const devInvoiceLinesOk =
    !!devInvoiceProduct &&
    proposedEntryLines.length > 0 &&
    proposedEntryLines.every((l) => parseFloat(l.amount_ht) > 0);

  const canGenerateDevInvoice = invoiceCanConfirm && devInvoiceHeaderOk && devInvoiceLinesOk;

  const goInvoiceWizardNext = async () => {
    if (invoiceWizardStep === 1) {
      if (!devInvoiceProduct) {
        toast.error(
          `Créez le produit « ${DEV_INVOICE_PRODUCT_NAME} » (catégorie « ${DEV_INVOICE_CATEGORY_NAME} ») pour continuer.`
        );
        return;
      }
      if (!invoiceCanConfirm || proposedEntryLines.length === 0 || !devInvoiceLinesOk) {
        toast.error(
          'Incluez au moins une société avec des lignes à montant HT positif, complétez les tarifs €/h manquants si l’aperçu le demande, puis réessayez.'
        );
        return;
      }
      const keys = billingCompanyKeysOrdered;
      if (keys.length === 0) {
        toast.error('Sélectionnez au moins une société à facturer.');
        return;
      }
      setInvoiceNumbersLoading(true);
      try {
        const res = await axios.post(
          `${API}/dev/billing/next-invoice-numbers`,
          { count: keys.length },
          { headers }
        );
        const nums = Array.isArray(res.data?.numbers) ? res.data.numbers : [];
        if (nums.length !== keys.length) {
          toast.error('Réponse serveur invalide pour les numéros de facture.');
          return;
        }
        setInvoiceNumbersReserved(nums);
        setEntryInvoiceData((p) => ({ ...p, invoice_number: nums[0] || '' }));
        setInvoiceWizardStep(2);
      } catch (e) {
        console.error(e);
        const d = e.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : 'Impossible de générer les numéros de facture.');
      } finally {
        setInvoiceNumbersLoading(false);
      }
      return;
    }
    if (invoiceWizardStep === 2) {
      if (!devInvoiceHeaderOk) {
        toast.error(
          'Vérifiez la date de facture et un taux de change valide si la devise n’est pas l’euro.'
        );
        return;
      }
      if (!(entryInvoiceData.payer_company_id || '').trim()) {
        toast.error('Choisissez le destinataire (société payeuse).');
        return;
      }
      setInvoiceWizardStep(3);
    }
  };

  const fetchDevInvoiceExchangeRate = useCallback(
    async (date, currency = 'USD') => {
      if (currency === 'EUR') {
        setEntryInvoiceData((prev) => ({ ...prev, exchange_rate: 1.0 }));
        setDevInvoiceExchangeInfo(null);
        return;
      }
      setDevInvoiceExchangeLoading(true);
      try {
        const response = await axios.get(`${API}/exchange-rate`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { from_currency: currency, to_currency: 'EUR', date },
        });
        const rate = response.data.rate;
        setEntryInvoiceData((prev) => ({ ...prev, exchange_rate: rate }));
        setDevInvoiceExchangeInfo({
          rate,
          date: response.data.date,
          source: response.data.source,
          warning: response.data.warning,
        });
      } catch (error) {
        console.error(error);
        const fallbackRate = 0.92;
        setEntryInvoiceData((prev) => ({ ...prev, exchange_rate: fallbackRate }));
        setDevInvoiceExchangeInfo({
          rate: fallbackRate,
          date,
          source: 'Taux de secours',
          warning: 'Impossible de récupérer le taux en ligne',
        });
        toast.warning('Utilisation du taux de change par défaut');
      } finally {
        setDevInvoiceExchangeLoading(false);
      }
    },
    [token]
  );

  const handleDevInvoiceCurrencyChange = async (newCurrency) => {
    if (newCurrency === 'EUR') {
      setEntryInvoiceData((prev) => ({ ...prev, currency: 'EUR', exchange_rate: 1.0 }));
      setDevInvoiceExchangeInfo(null);
      return;
    }
    setEntryInvoiceData((prev) => ({ ...prev, currency: newCurrency }));
    if (entryInvoiceData.invoice_date) {
      await fetchDevInvoiceExchangeRate(entryInvoiceData.invoice_date, newCurrency);
    }
  };

  const handleDevInvoiceInvoiceDateChange = async (newDate) => {
    setEntryInvoiceData((prev) => ({ ...prev, invoice_date: newDate }));
    if (entryInvoiceData.currency !== 'EUR' && newDate) {
      await fetchDevInvoiceExchangeRate(newDate, entryInvoiceData.currency);
    }
  };

  const buildDevInvoiceSnapForCompanyKey = useCallback(
    (ck) => {
      if (!proposedEntryLines.length) {
        return { ok: false, message: 'Validez d’abord les lignes à facturer (étape 1).' };
      }
      if (!(entryInvoiceData.invoice_number || '').trim()) {
        return { ok: false, message: 'Indiquez le n° de facture pour générer le document.' };
      }
      const keys = billingCompanyKeysOrdered;
      if (!ck || !keys.length || !keys.includes(ck)) {
        return { ok: false, message: 'Aucune société à facturer pour l’aperçu.' };
      }
      const linesForPreview = proposedEntryLines.filter((l) => l.company_key === ck);
      if (!linesForPreview.length) {
        return { ok: false, message: 'Aucune ligne pour cette société.' };
      }
      const idx = keys.indexOf(ck);
      const invoiceNumForPreview =
        invoiceNumbersReserved[idx] ?? (entryInvoiceData.invoice_number || '').trim();
      const cur = entryInvoiceData.currency || 'EUR';
      const xr = parseFloat(entryInvoiceData.exchange_rate) || 1;
      if (cur !== 'EUR') {
        if (!xr || Number.isNaN(xr) || xr <= 0) {
          return { ok: false, message: 'Renseignez un taux de change valide pour cette devise.' };
        }
      }
      return {
        ok: true,
        snap: {
          companyKey: ck,
          entryHeader: {
            invoice_number: invoiceNumForPreview,
            invoice_date: entryInvoiceData.invoice_date,
            supplier_name: (entryInvoiceData.supplier_name || '').trim(),
            payer_company_name: (entryInvoiceData.payer_company_name || '').trim(),
          },
          lines: linesForPreview.map((l) => ({ ...l })),
          options: {
            companies,
            logoUrl: `${window.location.origin}/prysme-logo.png`,
            currency: cur,
            exchangeRate: xr,
          },
        },
      };
    },
    [
      proposedEntryLines,
      billingCompanyKeysOrdered,
      invoiceNumbersReserved,
      entryInvoiceData,
      companies,
    ]
  );

  const handleGenerateAllDevInvoices = useCallback(async () => {
    const keys = billingCompanyKeysOrdered;
    if (keys.length < 2) return;
    if (invoiceNumbersReserved.length !== keys.length) {
      toast.error('Numéros de facture incomplets — repassez par l’étape précédente du wizard.');
      return;
    }
    if (!proposedEntryLines.length) {
      toast.error('Validez d’abord les lignes à facturer (étape 1).');
      return;
    }
    const cur = entryInvoiceData.currency || 'EUR';
    if (cur !== 'EUR') {
      const xr = parseFloat(entryInvoiceData.exchange_rate);
      if (!xr || Number.isNaN(xr) || xr <= 0) {
        toast.error('Renseignez un taux de change valide pour cette devise.');
        return;
      }
    }
    setDevInvoiceBatchLoading(true);
    setDevInvoiceBatchProgress({ current: 0, total: keys.length });
    let map = { ...invoiceFilesByCompanyKey };
    try {
      for (let i = 0; i < keys.length; i += 1) {
        const ck = keys[i];
        setDevInvoiceBatchProgress({ current: i + 1, total: keys.length });
        const built = buildDevInvoiceSnapForCompanyKey(ck);
        if (!built.ok) {
          toast.error(built.message);
          return;
        }
        try {
          map = await captureAndUploadDevInvoiceSnapshot(built.snap, map, token);
        } catch (err) {
          console.error(err);
          const label = companyNameByBillingKey[ck] || ck;
          if (
            err?.message === 'PDF_TOO_LARGE' ||
            err?.code === 'PDF_TOO_LARGE' ||
            err?.message === 'IMAGE_TOO_LARGE' ||
            err?.code === 'IMAGE_TOO_LARGE'
          ) {
            toast.error(`Fichier trop volumineux pour ${label} (max. 10 Mo).`);
          } else {
            toast.error(
              err.response?.data?.detail ||
                `Échec pour ${label} — corrigez et relancez, ou générez société par société.`
            );
          }
          return;
        }
        setInvoiceFilesByCompanyKey(map);
      }
      toast.success(
        `${keys.length} factures générées — pièces jointes PDF (A4) enregistrées pour chaque société.`
      );
    } finally {
      setDevInvoiceBatchLoading(false);
      setDevInvoiceBatchProgress(null);
    }
  }, [
    billingCompanyKeysOrdered,
    invoiceNumbersReserved,
    proposedEntryLines,
    entryInvoiceData,
    invoiceFilesByCompanyKey,
    token,
    buildDevInvoiceSnapForCompanyKey,
    companyNameByBillingKey,
  ]);

  const handleGenerateDevInvoicePdf = () => {
    const keys = billingCompanyKeysOrdered;
    const pck =
      invoicePreviewCompanyKey && keys.includes(invoicePreviewCompanyKey)
        ? invoicePreviewCompanyKey
        : keys[0];
    const built = buildDevInvoiceSnapForCompanyKey(pck);
    if (!built.ok) {
      toast.error(built.message);
      return;
    }
    setDevInvoicePreviewPayload({ ...built.snap, allowAttach: true });
  };

  const openDevInvoiceAttachedFile = useCallback(
    async (fileId, fileName) => {
      try {
        const response = await axios.get(`${API}/files/${fileId}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        let contentType = response.headers['content-type'] || 'application/octet-stream';
        if (contentType === 'application/octet-stream' && fileName) {
          const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
          const mimeTypes = {
            '.pdf': 'application/pdf',
            '.jpg': 'image/jpeg',
            '.jpeg': 'image/jpeg',
            '.png': 'image/png',
          };
          contentType = mimeTypes[ext] || contentType;
        }
        const blob = new Blob([response.data], { type: contentType });
        const blobUrl = window.URL.createObjectURL(blob);
        if (contentType.startsWith('image/') || contentType === 'application/pdf') {
          window.open(blobUrl, '_blank');
        } else {
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName || 'fichier';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de l'ouverture du fichier");
      }
    },
    [token]
  );

  const removeDevInvoiceAttachedFile = async (companyKey) => {
    const rec = invoiceFilesByCompanyKey[companyKey];
    const fid = rec?.file_id;
    if (!fid) return;
    try {
      await axios.delete(`${API}/files/${fid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la suppression du fichier');
      return;
    }
    setInvoiceFilesByCompanyKey((prev) => {
      const next = { ...prev };
      delete next[companyKey];
      return next;
    });
    toast.success('Pièce jointe retirée');
  };

  const handleAttachDevInvoice = async () => {
    const snap = devInvoicePreviewPayload;
    if (!snap?.entryHeader || !snap.lines?.length) {
      toast.error('Aucun aperçu de facture à joindre.');
      return;
    }
    if (snap.allowAttach === false) {
      toast.message('La facture est déjà enregistrée : la pièce jointe aurait dû être ajoutée avant l’enregistrement.');
      return;
    }
    const { entryHeader, lines, options, companyKey } = snap;
    const ck = companyKey || billingCompanyKeysOrdered[0];
    if (!ck) {
      toast.error('Société cible introuvable pour la pièce jointe.');
      return;
    }
    const cur = options.currency || 'EUR';
    if (cur !== 'EUR') {
      const xr = parseFloat(options.exchangeRate);
      if (!xr || Number.isNaN(xr) || xr <= 0) {
        toast.error('Taux de change invalide pour cette capture.');
        return;
      }
    }
    setDevInvoiceAttachLoading(true);
    try {
      const nextMap = await captureAndUploadDevInvoiceSnapshot(
        { companyKey: ck, entryHeader, lines, options },
        invoiceFilesByCompanyKey,
        token
      );
      setInvoiceFilesByCompanyKey(nextMap);
      toast.success('Pièce jointe enregistrée pour cette société (entrée produit correspondante).');
    } catch (error) {
      console.error(error);
      const d = error.response?.data?.detail;
      if (
        error?.message === 'PDF_TOO_LARGE' ||
        error?.code === 'PDF_TOO_LARGE' ||
        error?.message === 'IMAGE_TOO_LARGE' ||
        error?.code === 'IMAGE_TOO_LARGE'
      ) {
        toast.error('Fichier trop volumineux (max. 10 Mo). Réessayez avec moins de lignes ou contactez le support.');
      } else {
        toast.error(
          typeof d === 'string'
            ? d
            : "Impossible de générer la pièce jointe. Réessayez ou utilisez l'aperçu PDF."
        );
      }
    } finally {
      setDevInvoiceAttachLoading(false);
    }
  };

  const getInvoiceRateOverridesPayload = () => {
    const missingKeys = new Set();
    (preview?.companies || []).forEach((c) => {
      if (c.dev?.missing_rates) missingKeys.add(c.company_key);
      if (c.mep?.missing_rates) missingKeys.add(c.company_key);
    });
    const overrides = [];
    for (const ck of missingKeys) {
      const c = (preview.companies || []).find((x) => x.company_key === ck);
      if (!c) continue;
      const o = overrideRates[ck] || {};
      const ch = parseFloat(o.creation);
      const dh = parseFloat(o.debug);
      if (Number.isNaN(ch) || Number.isNaN(dh) || ch < 0 || dh < 0) {
        return { error: `Tarifs invalides pour ${c.company_name}`, overrides: [] };
      }
      overrides.push({
        company_id: c.company_id || null,
        company_name: c.company_name,
        creation_hourly: ch,
        debug_hourly: dh,
      });
    }
    return { error: null, overrides };
  };

  const finalizeDevEntryInvoice = async () => {
    if (!invoiceLineItemsPayload.length || !preview) return;
    if (!(entryInvoiceData.invoice_number || '').trim()) {
      toast.error('Indiquez le n° de facture (étape Informations).');
      setInvoiceWizardStep(2);
      return;
    }
    if (!(entryInvoiceData.payer_company_id || '').trim()) {
      toast.error(
        'Société destinataire introuvable : créez « CG DEVELOPPEMENT » dans les sociétés ou contactez un administrateur.'
      );
      setInvoiceWizardStep(2);
      return;
    }
    if (!devInvoiceProduct) {
      toast.error(
        `Produit « ${DEV_INVOICE_PRODUCT_NAME} » introuvable (catégorie « ${DEV_INVOICE_CATEGORY_NAME} »).`
      );
      setInvoiceWizardStep(1);
      return;
    }
    if (!devInvoiceLinesOk) {
      toast.error('Vérifiez les lignes incluses, les tarifs €/h et les montants (étape Lignes).');
      setInvoiceWizardStep(1);
      return;
    }
    const { error, overrides } = getInvoiceRateOverridesPayload();
    if (error) {
      toast.error(error);
      return;
    }
    const cur = entryInvoiceData.currency === 'USD' ? 'USD' : 'EUR';
    const xr = cur === 'EUR' ? 1.0 : parseFloat(entryInvoiceData.exchange_rate) || 1.0;
    const companyKeys = billingCompanyKeysOrdered;
    if (companyKeys.length === 0) {
      toast.error('Aucune société sélectionnée pour la facturation.');
      return;
    }
    if (invoiceNumbersReserved.length !== companyKeys.length) {
      toast.error('Numéros de facture manquants — repassez par l’étape « Informations » (Retour puis Suivant).');
      setInvoiceWizardStep(2);
      return;
    }
    const invoiceNumbersByCompanyKey = Object.fromEntries(
      companyKeys.map((k, i) => [k, invoiceNumbersReserved[i]])
    );
    /** Une seule ligne ponctuelle par société (somme des montants journal), pour Coûts ponctuels / entrées. */
    const mapMergedCompanyLinesToOneOffPayload = (companyLines) => {
      if (!companyLines.length) return null;
      const first = companyLines[0];
      const sumHtEur = companyLines.reduce((s, l) => s + (parseFloat(l.amount_ht) || 0), 0);
      if (sumHtEur <= 0) return null;
      const amount_ht = cur === 'EUR' ? sumHtEur : Math.round((sumHtEur / xr) * 100) / 100;
      const appNames = [
        ...new Set(companyLines.map((l) => (l.application_name || '').trim()).filter(Boolean)),
      ];
      const product_name =
        companyLines.length > 1
          ? appNames.length > 0
            ? `${first.product_name} (${appNames.join(' · ')})`
            : first.product_name
          : first.product_name;
      return {
        product_id: first.product_id || null,
        product_name,
        category_id: first.category_id || null,
        category_name: first.category_name || null,
        entry_type: 'one_off',
        billing_cycle: 'monthly',
        company_id: first.company_id || null,
        company_name: first.company_name || null,
        establishment_id: first.establishment_id || null,
        establishment_name: first.establishment_name || null,
        sector_id: first.sector_id || null,
        sector_name: first.sector_name || null,
        user_id: null,
        user_name: null,
        amount_ht,
        vat_rate: first.vat_rate,
        currency: cur,
        exchange_rate: xr,
      };
    };
    setSaving(true);
    try {
      for (const ck of companyKeys) {
        const companyLines = proposedEntryLines.filter((l) => l.company_key === ck);
        if (companyLines.length === 0) continue;
        const mergedLine = mapMergedCompanyLinesToOneOffPayload(companyLines);
        if (!mergedLine) continue;
        const att = invoiceFilesByCompanyKey[ck];
        await axios.post(
          `${API}/entries`,
          {
            invoice_number: invoiceNumbersByCompanyKey[ck],
            supplier_id: entryInvoiceData.supplier_id || null,
            supplier_name: entryInvoiceData.supplier_name.trim(),
            payer_company_id: entryInvoiceData.payer_company_id || null,
            payer_company_name: entryInvoiceData.payer_company_name.trim(),
            invoice_date: entryInvoiceData.invoice_date,
            file_id: att?.file_id || null,
            file_name: att?.file_name || null,
            currency: cur,
            exchange_rate: xr,
            lines: [mergedLine],
          },
          { headers }
        );
      }
      await axios.post(
        `${API}/dev/billing/execute`,
        {
          line_items: invoiceLineItemsPayload,
          invoice_number: entryInvoiceData.invoice_number.trim(),
          invoice_numbers_by_company_key: invoiceNumbersByCompanyKey,
          date: entryInvoiceData.invoice_date || undefined,
          rate_overrides: overrides,
          skip_cost_records: true,
        },
        { headers }
      );
      const firstKey = companyKeys[0];
      const linesSnap = proposedEntryLines
        .filter((l) => l.company_key === firstKey)
        .map((l) => ({ ...l }));
      setDevInvoicePreviewPayload({
        companyKey: firstKey,
        entryHeader: {
          invoice_number: invoiceNumbersByCompanyKey[firstKey],
          invoice_date: entryInvoiceData.invoice_date,
          supplier_name: entryInvoiceData.supplier_name.trim(),
          payer_company_name: entryInvoiceData.payer_company_name.trim(),
        },
        lines: linesSnap.length ? linesSnap : proposedEntryLines.map((l) => ({ ...l })),
        options: {
          companies,
          logoUrl: `${window.location.origin}/prysme-logo.png`,
          currency: cur,
          exchangeRate: xr,
        },
        allowAttach: false,
      });
      toast.success(
        companyKeys.length > 1
          ? `${companyKeys.length} entrées produit créées (1 ligne ponctuelle agrégée par société) — journal marqué facturé.`
          : 'Entrée produit créée (1 ligne ponctuelle agrégée) — temps interne marqué facturé.'
      );
      setInvoiceOpen(false);
      setPreview(null);
      loadEntries();
      loadJournalFilterOptions();
      fetchUnbilledAndRates().catch(() => {});
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur lors de la facturation');
    } finally {
      setSaving(false);
    }
  };

  /** Totaux € temps (tarifs actuels), toutes sociétés et applications — alignés sur company-dashboard */
  const journalAllTotals = useMemo(() => {
    let unbilled = 0;
    let billed = 0;
    let unbilledMiss = false;
    let billedMiss = false;
    for (const co of companyDashboard) {
      unbilled += Number(co.total_unbilled_eur) || 0;
      billed += Number(co.total_billed_eur) || 0;
      if (co.total_unbilled_missing_rates) unbilledMiss = true;
      if (co.total_billed_missing_rates) billedMiss = true;
    }
    return {
      unbilled: Math.round(unbilled * 100) / 100,
      billed: Math.round(billed * 100) / 100,
      unbilledMiss,
      billedMiss,
    };
  }, [companyDashboard]);

  const devDashAmount = (value, missing) => {
    const n = Number(value);
    const v = Number.isFinite(n) ? n : 0;
    if (missing && v === 0) {
      return '—';
    }
    return (
      <span
        title={
          missing
            ? 'Certaines heures n’ont pas été valorisées (tarifs €/h incomplets pour cette société)'
            : undefined
        }
      >
        {v.toFixed(2)} €
      </span>
    );
  };

  if (!canAccess) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-6" data-testid="development-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <Code2 className="w-8 h-8 text-primary" />
            Développement
          </h1>
          <p className="text-muted-foreground mt-1">
            Saisie du temps interne, tarifs par société et facturation vers les coûts ponctuels (fournisseur{' '}
            <span className="text-foreground">CG Développement</span>).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="default"
            className="gap-2"
            onClick={() => {
              setEditingDevEntryId(null);
              setLines([emptyLine()]);
              setNewOpen(true);
            }}
            data-testid="dev-new-entry"
          >
            <FilePlus2 className="w-4 h-4" />
            Nouvelle entrée
          </Button>
          <Button variant="secondary" className="gap-2" onClick={openInvoice} data-testid="dev-invoice">
            <Receipt className="w-4 h-4" />
            Facturer
          </Button>
          <Button variant="outline" className="gap-2" onClick={openSettings} data-testid="dev-settings">
            <Settings2 className="w-4 h-4" />
            Paramètres
          </Button>
        </div>
      </div>

      {companyDashboard.length > 0 ? (
        <div className="grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 sm:gap-2.5">
          {companyDashboard.map((co) => (
            <Card
              key={co.company_key}
              className="border-border min-w-0 overflow-hidden shadow-sm ring-1 ring-border/40 bg-card flex flex-col"
            >
              <CardHeader className="py-1.5 px-2 sm:px-2.5 border-b border-border/70 bg-muted/20 shrink-0 space-y-0">
                <CardTitle
                  className="text-xs sm:text-[13px] font-semibold leading-snug text-foreground line-clamp-2"
                  title={co.company_name}
                >
                  {co.company_name}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 flex-1 min-h-0 flex flex-col">
                <div className="overflow-x-auto overflow-y-auto max-h-[11rem] sm:max-h-[13rem] overscroll-contain">
                  <table className="w-full min-w-[200px] table-fixed text-[11px] sm:text-xs leading-tight">
                    <thead className="sticky top-0 z-[1]">
                      <tr className="border-b border-border/70 bg-card shadow-[0_1px_0_0_hsl(var(--border))]">
                        <th className="text-left font-medium py-1 pl-2 pr-1 w-[45%]" />
                        <th
                          className="text-right font-semibold py-1 px-1.5 sm:px-2 bg-orange-500/12 text-orange-700 dark:text-orange-400 border-l border-orange-500/20 w-[27.5%]"
                          title="Non facturé"
                        >
                          Non fact.
                        </th>
                        <th
                          className="text-right font-semibold py-1 pr-2 pl-1.5 sm:pl-2 bg-emerald-500/12 text-emerald-700 dark:text-emerald-400 border-l border-emerald-500/20 w-[27.5%]"
                          title="Facturé"
                        >
                          Fact.
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {co.applications.map((a) => (
                        <tr
                          key={a.application_name}
                          className="border-t border-border/40 hover:bg-muted/15"
                        >
                          <td
                            className="py-0.5 pl-2 pr-1 font-medium text-foreground align-middle truncate max-w-0"
                            title={a.application_name}
                          >
                            {a.application_name}
                          </td>
                          <td className="text-right tabular-nums py-0.5 px-1.5 sm:px-2 align-middle bg-orange-500/[0.05] text-orange-700 dark:text-orange-300 border-l border-orange-500/15">
                            {devDashAmount(a.unbilled_eur, a.unbilled_missing_rates)}
                          </td>
                          <td className="text-right tabular-nums py-0.5 pr-2 pl-1.5 sm:pl-2 align-middle bg-emerald-500/[0.05] text-emerald-700 dark:text-emerald-300 border-l border-emerald-500/15">
                            {devDashAmount(a.billed_eur, a.billed_missing_rates)}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t border-border bg-muted/30">
                        <td className="py-1 pl-2 pr-1 font-bold text-foreground align-middle">Total</td>
                        <td className="text-right tabular-nums py-1 px-1.5 sm:px-2 font-bold bg-orange-500/12 text-orange-800 dark:text-orange-300 border-l border-orange-500/20 align-middle">
                          {devDashAmount(co.total_unbilled_eur, co.total_unbilled_missing_rates)}
                        </td>
                        <td className="text-right tabular-nums py-1 pr-2 pl-1.5 sm:pl-2 font-bold bg-emerald-500/12 text-emerald-800 dark:text-emerald-300 border-l border-emerald-500/20 align-middle">
                          {devDashAmount(co.total_billed_eur, co.total_billed_missing_rates)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <Card className="bg-card border-border">
        <CardHeader className="space-y-3 p-6 pb-0">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
            <div className="min-w-0 flex items-center">
              <CardTitle>Journal des entrées</CardTitle>
            </div>
            <div
              className="flex flex-wrap gap-2 shrink-0"
              title="Montants temps au tarif €/h actuel, toutes sociétés et applications confondues"
            >
              <div className="rounded-md border border-orange-500/30 bg-orange-500/10 px-2.5 py-2 min-w-[8.5rem]">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-orange-700 dark:text-orange-400 leading-none">
                  Non facturé (total)
                </p>
                <div className="text-base font-bold tabular-nums text-orange-900 dark:text-orange-200 leading-tight mt-1">
                  {devDashAmount(journalAllTotals.unbilled, journalAllTotals.unbilledMiss)}
                </div>
              </div>
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-2 min-w-[8.5rem]">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400 leading-none">
                  Facturé (total)
                </p>
                <div className="text-base font-bold tabular-nums text-emerald-900 dark:text-emerald-200 leading-tight mt-1">
                  {devDashAmount(journalAllTotals.billed, journalAllTotals.billedMiss)}
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-6 pb-6 pt-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end mb-4">
                <div className="space-y-1.5 min-w-0 flex-1 sm:min-w-[220px] sm:max-w-sm">
                  <Label className="text-xs">Société</Label>
                  <Combobox
                    options={journalCompanyFilterOptions}
                    value={journalFilterCompany}
                    onValueChange={(v) => {
                      setPage(1);
                      setJournalFilterCompany(v);
                    }}
                    placeholder="Toutes les sociétés"
                    searchPlaceholder="Filtrer les sociétés…"
                    emptyText="Aucune société trouvée."
                  />
                </div>
                <div className="space-y-1.5 min-w-0 flex-1 sm:min-w-[220px] sm:max-w-sm">
                  <Label className="text-xs">Application</Label>
                  <Combobox
                    options={journalApplicationFilterOptions}
                    value={journalFilterApplication}
                    onValueChange={(v) => {
                      setPage(1);
                      setJournalFilterApplication(v);
                    }}
                    placeholder="Toutes les applications"
                    searchPlaceholder="Filtrer les applications…"
                    emptyText="Aucune application trouvée."
                  />
                </div>
                {(journalFilterCompany || journalFilterApplication) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 w-full sm:w-auto"
                    onClick={() => {
                      setPage(1);
                      setJournalFilterCompany('');
                      setJournalFilterApplication('');
                    }}
                  >
                    Réinitialiser les filtres
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground -mt-2 mb-3">
                Les filtres portent sur tout le journal ; la pagination affiche les entrées correspondantes. Les totaux
                en-tête restent globaux.
              </p>
              <div className="rounded-md border border-border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead>Date de travail</TableHead>
                      <TableHead>Utilisateur</TableHead>
                      <TableHead>Lignes</TableHead>
                      <TableHead title="Durée totale (créneaux × 30 min)">Création</TableHead>
                      <TableHead title="Durée totale (créneaux × 15 min)">Débogage</TableHead>
                      <TableHead>Détail</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Prix total</TableHead>
                      <TableHead className="w-24 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground py-12">
                          Aucune entrée pour l’instant
                        </TableCell>
                      </TableRow>
                    ) : journalFilteredEntries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground py-12">
                          Aucune ligne ne correspond aux filtres sur cette page.
                        </TableCell>
                      </TableRow>
                    ) : (
                      journalFilteredEntries.map(({ row, displayLines }) => {
                        const ls = displayLines;
                        const cSlots = ls.reduce(
                          (s, l) => s + (parseInt(l.creation_slots, 10) || 0),
                          0
                        );
                        const dSlots = ls.reduce(
                          (s, l) => s + (parseInt(l.debug_slots, 10) || 0),
                          0
                        );
                        const { sum: entryTotalEur, hasValued, hasUnpricedWork } = journalEntryLinesTotalEuro(
                          ls,
                          invoiceRatesMap
                        );
                        return (
                          <TableRow key={row.id} className="border-border">
                            <TableCell className="align-top text-muted-foreground">
                              <div className="flex flex-col gap-1 min-w-[7rem]">
                                {ls.map((l, li) => (
                                  <div key={li} className="text-sm whitespace-nowrap">
                                    {displayWorkPeriodFr(
                                      l.work_date,
                                      l.work_date_granularity,
                                      row.created_at
                                    ) ?? '—'}
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{row.user_name || '—'}</TableCell>
                            <TableCell>{ls.length}</TableCell>
                            <TableCell className="tabular-nums whitespace-nowrap">
                              {formatSlotsAsXxhXX(cSlots, 30)}
                            </TableCell>
                            <TableCell className="tabular-nums whitespace-nowrap">
                              {formatSlotsAsXxhXX(dSlots, 15)}
                            </TableCell>
                            <TableCell className="max-w-md">
                              <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                                {ls.map((l, i) => (
                                  <div key={i} className="border-l-2 border-primary/40 pl-2">
                                    <span className="text-foreground font-medium">{l.application_name}</span>
                                    {' · '}
                                    {l.company_name}
                                    {' · '}
                                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                                      {l.billed ? 'Facturé' : 'Non facturé'}
                                    </Badge>
                                    <span className="block text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                                      Création {formatSlotsAsXxhXX(l.creation_slots, 30)} · Débogage{' '}
                                      {formatSlotsAsXxhXX(l.debug_slots, 15)}
                                    </span>
                                    {l.comment ? (
                                      <span className="block text-muted-foreground mt-0.5">{l.comment}</span>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="align-top text-right tabular-nums whitespace-nowrap font-medium">
                              {!hasValued && hasUnpricedWork ? (
                                <span
                                  className="text-muted-foreground"
                                  title="Tarifs €/h incomplets pour au moins une société — renseignez-les dans Paramètres développement."
                                >
                                  —
                                </span>
                              ) : (
                                <span
                                  className={hasUnpricedWork ? 'text-amber-600' : ''}
                                  title={
                                    hasUnpricedWork
                                      ? 'Total partiel : certaines lignes n’ont pas pu être valorisées (tarifs manquants).'
                                      : undefined
                                  }
                                >
                                  {entryTotalEur.toFixed(2)} €
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="align-top text-right">
                              <div className="flex justify-end gap-0.5">
                                {ls.every((l) => !l.billed) ? (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="shrink-0"
                                    title="Modifier cette entrée (uniquement si non facturée)"
                                    onClick={() => openEditDevEntry(row)}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                ) : null}
                                {isAdmin ? (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="text-destructive hover:text-destructive shrink-0"
                                    title="Supprimer cette entrée (admin)"
                                    onClick={() =>
                                      setDevEntryDeleteTarget({
                                        id: row.id,
                                        label: row.user_name || row.id,
                                      })
                                    }
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                ) : null}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {total > pageSize && (
                <Pagination className="mt-6">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
                        onClick={(e) => {
                          e.preventDefault();
                          if (page > 1) setPage((p) => p - 1);
                        }}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink href="#" isActive>
                        {page} / {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                        onClick={(e) => {
                          e.preventDefault();
                          if (page < totalPages) setPage((p) => p + 1);
                        }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Nouvelle entrée */}
      <Dialog
        open={newOpen}
        onOpenChange={(open) => {
          setNewOpen(open);
          if (!open) setEditingDevEntryId(null);
        }}
      >
        <DialogContent
          className={`bg-card border-border max-w-3xl h-[min(88dvh,720px)] ${devModalShell}`}
        >
          <DialogHeader className={devModalHeader}>
            <DialogTitle>{editingDevEntryId ? 'Modifier l’entrée' : 'Nouvelle entrée'}</DialogTitle>
            <DialogDescription>
              {editingDevEntryId ? (
                <>
                  Mettez à jour les lignes tant qu’<strong>aucune</strong> n’a été facturée. Les lignes modifiées
                  restent <strong>non facturées</strong>.
                </>
              ) : (
                <>
                  Utilisateur : <strong>{user?.name || user?.email}</strong>. Chaque ligne : application, date du travail,
                  créneaux création / débogage, commentaire, et <strong>plusieurs sociétés</strong>. À l’enregistrement,
                  les créneaux sont <strong>répartis équitablement</strong> entre les sociétés (la valorisation temps €/h
                  est ainsi divisée par le nombre de sociétés).
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className={`${devModalBody} space-y-4`}>
            {lines.map((line, idx) => (
              <div
                key={idx}
                className="rounded-lg border border-border p-4 space-y-3 bg-secondary/20"
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-foreground">Ligne {idx + 1}</span>
                  {lines.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Date de travail</Label>
                    <RadioGroup
                      value={line.work_date_granularity === 'month' ? 'month' : 'day'}
                      onValueChange={(v) => {
                        if (v === 'month') {
                          const cur = (line.work_date || todayISO()).trim();
                          const ym = /^\d{4}-\d{2}/.test(cur) ? cur.slice(0, 7) : todayISO().slice(0, 7);
                          updateLine(idx, { work_date_granularity: 'month', work_date: `${ym}-01` });
                        } else {
                          const cur = (line.work_date || todayISO()).trim();
                          const day = /^\d{4}-\d{2}-\d{2}$/.test(cur) ? cur : todayISO();
                          updateLine(idx, { work_date_granularity: 'day', work_date: day });
                        }
                      }}
                      className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-6"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="day" id={`dev-wd-day-${idx}`} />
                        <Label htmlFor={`dev-wd-day-${idx}`} className="font-normal cursor-pointer">
                          Jour précis
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="month" id={`dev-wd-month-${idx}`} />
                        <Label htmlFor={`dev-wd-month-${idx}`} className="font-normal cursor-pointer">
                          Mois entier
                        </Label>
                      </div>
                    </RadioGroup>
                    {line.work_date_granularity === 'month' ? (
                      <Input
                        type="month"
                        className="bg-background max-w-xs"
                        value={(line.work_date || '').trim().slice(0, 7)}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v) updateLine(idx, { work_date: `${v}-01`, work_date_granularity: 'month' });
                        }}
                      />
                    ) : (
                      <Input
                        type="date"
                        value={line.work_date || todayISO()}
                        onChange={(e) =>
                          updateLine(idx, { work_date: e.target.value, work_date_granularity: 'day' })
                        }
                        className="bg-background max-w-xs"
                      />
                    )}
                    <p className="text-xs text-muted-foreground">
                      Jour : date exacte. Mois : toute la période (affichée comme « mars 2025 »).
                    </p>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Application</Label>
                    <Combobox
                      options={applicationComboboxOptions}
                      value={(line.application_name || '').trim()}
                      onValueChange={(v) => updateLine(idx, { application_name: v })}
                      placeholder="Rechercher une application…"
                      searchPlaceholder="Filtrer la liste…"
                      emptyText="Aucune application trouvée."
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Sociétés</Label>
                    <div className="flex flex-wrap gap-2 min-h-9 items-center rounded-md border border-border bg-background/50 px-2 py-2">
                      {(line.companies || []).length === 0 ? (
                        <span className="text-sm text-muted-foreground px-1">
                          Aucune — ajoutez-en avec la liste ci-dessous.
                        </span>
                      ) : (
                        (line.companies || []).map((c) => {
                          const ck = devCompanyKey(c.company_id, c.company_name);
                          return (
                            <Badge
                              key={ck}
                              variant="secondary"
                              className="max-w-full gap-1 pl-2.5 pr-0.5 py-0.5 font-normal"
                            >
                              <span className="truncate max-w-[12rem] sm:max-w-[16rem]">
                                {c.company_name || '—'}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 shrink-0 rounded-sm"
                                onClick={() => removeCompanyFromLine(idx, ck)}
                                aria-label={`Retirer ${c.company_name || 'société'}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </Badge>
                          );
                        })
                      )}
                    </div>
                    <Combobox
                      key={`add-company-${idx}-${(line.companies || []).length}`}
                      options={companyComboboxAddOptions}
                      value=""
                      onValueChange={(v) => {
                        if (v) addCompanyToLine(idx, v);
                      }}
                      placeholder="Ajouter une société…"
                      searchPlaceholder="Filtrer la liste…"
                      emptyText="Aucune société trouvée."
                    />
                    <p className="text-xs text-muted-foreground">
                      À l’enregistrement : une ligne de temps par société ; les créneaux saisis sont répartis entre
                      elles (ex. 6 créneaux créa et 3 sociétés → 2 créneaux chacune), ce qui divise le coût horaire au
                      prorata.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-start gap-6">
                  <div className="space-y-1">
                    <Label className="text-xs">Création (× 30 min)</Label>
                    <div className="flex items-center gap-2">
                      <DevSlotRepeatButton
                        onStep={() => bumpLineSlot(idx, 'creation_slots', -1)}
                        disabled={(line.creation_slots || 0) <= 0}
                      >
                        <Minus className="w-4 h-4" />
                      </DevSlotRepeatButton>
                      <span className="min-w-[3.5rem] text-center font-mono text-sm tabular-nums font-medium text-foreground">
                        {formatSlotsAsXxhXX(line.creation_slots, 30)}
                      </span>
                      <DevSlotRepeatButton onStep={() => bumpLineSlot(idx, 'creation_slots', 1)}>
                        <Plus className="w-4 h-4" />
                      </DevSlotRepeatButton>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Débogage (× 15 min)</Label>
                    <div className="flex items-center gap-2">
                      <DevSlotRepeatButton
                        onStep={() => bumpLineSlot(idx, 'debug_slots', -1)}
                        disabled={(line.debug_slots || 0) <= 0}
                      >
                        <Minus className="w-4 h-4" />
                      </DevSlotRepeatButton>
                      <span className="min-w-[3.5rem] text-center font-mono text-sm tabular-nums font-medium text-foreground">
                        {formatSlotsAsXxhXX(line.debug_slots, 15)}
                      </span>
                      <DevSlotRepeatButton onStep={() => bumpLineSlot(idx, 'debug_slots', 1)}>
                        <Plus className="w-4 h-4" />
                      </DevSlotRepeatButton>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Commentaire</Label>
                  <Textarea
                    value={line.comment}
                    onChange={(e) => updateLine(idx, { comment: e.target.value })}
                    placeholder="Optionnel"
                    rows={2}
                    className="bg-background"
                  />
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2"
              onClick={() => setLines((prev) => [...prev, emptyLine()])}
            >
              <Plus className="w-4 h-4" />
              Ajouter une ligne
            </Button>
          </div>
          <DialogFooter className={devModalFooter}>
            <Button
              variant="outline"
              onClick={() => {
                setNewOpen(false);
                setEditingDevEntryId(null);
              }}
            >
              Annuler
            </Button>
            <Button onClick={submitNewEntry} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : editingDevEntryId ? (
                'Enregistrer les modifications'
              ) : (
                'Enregistrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paramètres tarifs */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent
          className={`bg-card border-border max-w-4xl h-[min(88dvh,760px)] ${devModalShell}`}
        >
          <DialogHeader className={devModalHeader}>
            <DialogTitle>Paramètres</DialogTitle>
            <DialogDescription>
              Grille €/h (création et débogage) par société. La mise en production se règle dans{' '}
              <strong>Facturer</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className={`${devModalBody} space-y-6`}>
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Coût horaire par société</h3>
              <div className="rounded-md border border-border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Société</TableHead>
                      <TableHead>€ / h création</TableHead>
                      <TableHead>€ / h débogage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rateRows.map((r, i) => (
                      <TableRow key={r.company_id || r.company_name}>
                        <TableCell className="font-medium">{r.company_name}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            value={r.creation_hourly}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              setRateRows((prev) => {
                                const n = [...prev];
                                n[i] = { ...n[i], creation_hourly: Number.isNaN(v) ? 0 : v };
                                return n;
                              });
                            }}
                            className="bg-background w-32"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            value={r.debug_hourly}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              setRateRows((prev) => {
                                const n = [...prev];
                                n[i] = { ...n[i], debug_hourly: Number.isNaN(v) ? 0 : v };
                                return n;
                              });
                            }}
                            className="bg-background w-32"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
          <DialogFooter className="shrink-0 flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-2 px-5 py-4 sm:px-6 border-t border-border/60 bg-card">
            <label className="flex items-start gap-2 cursor-pointer select-none text-sm flex-1 min-w-0 sm:min-w-[12rem] pr-1">
              <Checkbox
                checked={applyNewRatesToExistingLines}
                onCheckedChange={(chk) => setApplyNewRatesToExistingLines(chk === true)}
                className="mt-0.5 shrink-0"
                aria-label="Appliquer les nouveaux tarifs aux entrées existantes"
              />
              <span className="min-w-0 leading-snug">
                <span className="font-medium text-foreground">Changer le prix des entrées actuelles</span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Non coché : entrées déjà saisies inchangées. Coché : tout le journal aux nouveaux tarifs.
                </span>
              </span>
            </label>
            <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setSettingsOpen(false)}>
                Fermer
              </Button>
              <Button onClick={saveSettings} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Facturation : entrée produit + clôture journal (sans doublon coûts ponctuels) */}
      <Dialog
        open={invoiceOpen}
        onOpenChange={(open) => {
          setInvoiceOpen(open);
          if (!open) {
            setInvoiceWizardStep(1);
            setInvoiceBootstrapLoading(false);
          }
        }}
      >
        <DialogContent
          className={`bg-card border-border max-w-6xl h-[min(92dvh,900px)] ${devModalShell}`}
        >
          <DialogHeader className={devModalHeader}>
            <DialogTitle className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span>Facturer — entrée produit</span>
              <div className="flex items-center gap-2 shrink-0">
                {[1, 2, 3].map((st) => (
                  <div
                    key={st}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      invoiceWizardStep === st
                        ? 'bg-primary text-primary-foreground'
                        : invoiceWizardStep > st
                          ? 'bg-green-500 text-white'
                          : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {invoiceWizardStep > st ? <Check className="w-4 h-4" /> : st}
                  </div>
                ))}
              </div>
            </DialogTitle>
            <DialogDescription>
              {invoiceWizardStep === 1 &&
                `Étape 1 — Une ligne par société cliente : regroupe toutes les applications et saisies non facturées. Une facture et une entrée produit seront créées par société (payeur : CG DEVELOPPEMENT). Déplier pour le journal, la MEP et l’aperçu « ${DEV_INVOICE_PRODUCT_NAME} ».`}
              {invoiceWizardStep === 2 &&
                'Étape 2 — N° (série globale), date, devise, fournisseur, payeur CG DEVELOPPEMENT ; aperçu / PJ par société cliente.'}
              {invoiceWizardStep === 3 &&
                'Étape 3 — Récapitulatif : une entrée produit par société, une seule ligne ponctuelle agrégée par société (toutes les applications regroupées), numéros de facture distincts. Le journal conserve le détail par ligne.'}
            </DialogDescription>
          </DialogHeader>
          <div className={`${devModalBody} space-y-4 relative`}>
            {invoiceBootstrapLoading ? (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-background/92 backdrop-blur-sm rounded-md">
                <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
                <p className="text-sm text-muted-foreground text-center px-4">
                  Chargement des données de facturation…
                </p>
              </div>
            ) : null}
            {invoiceWizardStep === 1 && (
              <div className="space-y-4">
            {!devInvoiceProduct && (devBillingRefs.products || []).length > 0 ? (
              <p className="text-sm text-amber-600 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2">
                Créez le produit « {DEV_INVOICE_PRODUCT_NAME} » (catégorie « {DEV_INVOICE_CATEGORY_NAME} ») pour générer
                les lignes d’entrée.
              </p>
            ) : null}
            <div className="rounded-lg border border-border p-3 bg-secondary/20 space-y-2">
              <p className="text-sm font-medium text-foreground">Lignes non facturées</p>
              {unbilledLines.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune ligne à facturer pour le moment.</p>
              ) : (
                <div className="text-sm space-y-2">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="h-9 w-10 p-2 text-center" title="Inclure dans la facture">
                          <span className="sr-only">Inclure</span>
                        </TableHead>
                        <TableHead className="h-9 w-9 p-2" />
                        <TableHead className="h-9">Société (client)</TableHead>
                        <TableHead className="h-9">Applications</TableHead>
                        <TableHead className="h-9 text-center">Heures créa</TableHead>
                        <TableHead className="h-9 text-center">Heures dbg</TableHead>
                        <TableHead className="h-9 text-right">Est. temps (€)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {unbilledCompanyBundles.map((g) => {
                        const expanded = !!expandedBundleKeys[g.key];
                        const appLabels = [
                          ...new Set(
                            g.lines.map((l) => (l.application_name || '').trim()).filter(Boolean)
                          ),
                        ].sort((a, b) => a.localeCompare(b, 'fr'));
                        const est = bundleEstimateTimeOnly(g);
                        const sub = bundleDetailSubtotal(g);
                        const buMep = bundleMepUi[g.key] || {};
                        const recapLines = groupIncluded[g.key]
                          ? g.lines.filter(
                              (l) =>
                                invoiceLineIncluded[journalLineKey(l.entry_id, l.line_index)] !== false
                            )
                          : g.lines;
                        const recapCSlots = recapLines.reduce(
                          (s, l) => s + (parseInt(l.creation_slots, 10) || 0),
                          0
                        );
                        const recapDSlots = recapLines.reduce(
                          (s, l) => s + (parseInt(l.debug_slots, 10) || 0),
                          0
                        );
                        const bundleHasMissingRates = g.lines.some((l) => {
                          const c = parseInt(l.creation_slots, 10) || 0;
                          const d = parseInt(l.debug_slots, 10) || 0;
                          if (!c && !d) return false;
                          return lineTimeEuroFromMaps(l, invoiceRatesMap) === null;
                        });
                        return (
                          <React.Fragment key={g.key}>
                            <TableRow
                              className="cursor-pointer hover:bg-muted/40"
                              onClick={() => toggleBundleExpand(g.key)}
                            >
                              <TableCell
                                className="p-2 w-10 align-middle text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  checked={!!groupIncluded[g.key]}
                                  onCheckedChange={(chk) => {
                                    const on = chk === true;
                                    setGroupIncluded((prev) => ({ ...prev, [g.key]: on }));
                                    setExpandedBundleKeys((prev) => ({ ...prev, [g.key]: on }));
                                    if (on) {
                                      setInvoiceLineIncluded((prev) => {
                                        const next = { ...prev };
                                        for (const l of g.lines) {
                                          delete next[journalLineKey(l.entry_id, l.line_index)];
                                        }
                                        return next;
                                      });
                                    }
                                  }}
                                  aria-label="Inclure cette société dans la facturation"
                                />
                              </TableCell>
                              <TableCell className="p-2 w-9 align-middle">
                                <ChevronDown
                                  className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`}
                                  aria-hidden
                                />
                              </TableCell>
                              <TableCell className="py-2 font-medium">{g.company_name}</TableCell>
                              <TableCell
                                className="py-2 text-sm text-muted-foreground max-w-[min(100%,18rem)] truncate"
                                title={appLabels.join(', ')}
                              >
                                {appLabels.length ? appLabels.join(', ') : '—'}
                              </TableCell>
                              <TableCell className="py-2 text-center tabular-nums text-muted-foreground">
                                {formatSlotsAsXxhXX(recapCSlots, DEV_CREATION_SLOT_HOURS * 60)}
                              </TableCell>
                              <TableCell className="py-2 text-center tabular-nums text-muted-foreground">
                                {formatSlotsAsXxhXX(recapDSlots, DEV_DEBUG_SLOT_HOURS * 60)}
                              </TableCell>
                              <TableCell className="py-2 text-right tabular-nums">
                                {est != null ? `${est} €` : '—'}
                              </TableCell>
                            </TableRow>
                            {expanded ? (
                              <TableRow className="hover:bg-transparent">
                                <TableCell colSpan={7} className="p-0 border-b">
                                  <div
                                    className="bg-muted/25 border-t border-border/60 px-3 py-3 space-y-3"
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    role="presentation"
                                  >
                                    {bundleHasMissingRates ? (
                                      <p className="text-sm text-amber-600">
                                        Tarifs incomplets pour au moins une ligne de cette société — renseignez les €/h
                                        dans Paramètres ou ci-dessous (aperçu).
                                      </p>
                                    ) : null}
                                    <div className="rounded-md border border-border/70 bg-background/70 px-3 py-2.5 space-y-2">
                                      <p className="text-xs font-medium text-muted-foreground">
                                        Mise en production — forfait pour toute la société{' '}
                                        <span className="text-foreground">{g.company_name}</span>
                                        {appLabels.length ? (
                                          <span className="block mt-1 font-normal text-muted-foreground">
                                            Applications concernées : {appLabels.join(', ')}
                                          </span>
                                        ) : null}
                                      </p>
                                      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                                        <label className="flex items-center gap-2 cursor-pointer select-none text-sm shrink-0">
                                          <Checkbox
                                            checked={!!buMep.mep}
                                            onCheckedChange={(chk) => {
                                              if (chk === true) {
                                                patchBundleMepUi(g.key, { mep: true });
                                              } else {
                                                patchBundleMepUi(g.key, { mep: false, production: '' });
                                              }
                                            }}
                                            aria-label="Mise en production pour ce bloc"
                                          />
                                          <span className="font-medium">Facturer la mise en production</span>
                                        </label>
                                        <div className="flex flex-wrap items-center gap-2 min-w-0">
                                          <Label className="text-xs whitespace-nowrap shrink-0">
                                            Prix production (€)
                                          </Label>
                                          <Input
                                            type="number"
                                            min={0}
                                            step={0.01}
                                            disabled={!buMep.mep}
                                            value={buMep.production ?? ''}
                                            onChange={(e) =>
                                              patchBundleMepUi(g.key, { production: e.target.value })
                                            }
                                            placeholder="0"
                                            className="bg-background h-9 w-28 max-w-full"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    {groupIncluded[g.key] ? (
                                      <p className="text-xs text-muted-foreground">
                                        Décochez les lignes à exclure pour cette société.
                                      </p>
                                    ) : null}
                                    <div className="rounded-md border border-border/80 bg-background/80 overflow-x-auto">
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead className="h-8 w-10 p-2 text-center" title="Inclure la ligne">
                                              <span className="sr-only">Ligne</span>
                                            </TableHead>
                                            <TableHead className="h-8 whitespace-nowrap">Date de travail</TableHead>
                                            <TableHead className="h-8 text-right whitespace-nowrap">€/h créa</TableHead>
                                            <TableHead className="h-8 text-right whitespace-nowrap">€/h dbg</TableHead>
                                            <TableHead className="h-8 text-center whitespace-nowrap">Heures créa</TableHead>
                                            <TableHead className="h-8 text-center whitespace-nowrap">Heures dbg</TableHead>
                                            <TableHead className="h-8 text-right whitespace-nowrap">Temps (€)</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {g.lines.map((l) => {
                                            const k = journalLineKey(l.entry_id, l.line_index);
                                            const timeEur = lineTimeEuro(l);
                                            const lr = lineEffectiveHourlyRates(l, invoiceRatesMap);
                                            const linePicked =
                                              !groupIncluded[g.key] || invoiceLineIncluded[k] !== false;
                                            return (
                                              <TableRow key={k}>
                                                <TableCell
                                                  className="p-2 w-10 align-middle text-center"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <Checkbox
                                                    checked={linePicked}
                                                    disabled={!groupIncluded[g.key]}
                                                    onCheckedChange={(chk) => {
                                                      if (!groupIncluded[g.key]) return;
                                                      setInvoiceLineIncluded((prev) => {
                                                        const next = { ...prev };
                                                        if (chk === true) {
                                                          delete next[k];
                                                        } else {
                                                          next[k] = false;
                                                        }
                                                        return next;
                                                      });
                                                    }}
                                                    aria-label="Inclure cette ligne dans le lot"
                                                  />
                                                </TableCell>
                                                <TableCell className="py-2 whitespace-nowrap tabular-nums font-medium">
                                                  {displayWorkPeriodFr(
                                                    l.work_date,
                                                    l.work_date_granularity,
                                                    l.created_at
                                                  ) ?? '—'}
                                                </TableCell>
                                                <TableCell className="py-2 text-right tabular-nums text-muted-foreground">
                                                  {lr ? `${lr.creation_hourly} €` : '—'}
                                                </TableCell>
                                                <TableCell className="py-2 text-right tabular-nums text-muted-foreground">
                                                  {lr ? `${lr.debug_hourly} €` : '—'}
                                                </TableCell>
                                                <TableCell className="py-2 text-center tabular-nums text-muted-foreground">
                                                  {formatSlotsAsXxhXX(
                                                    l.creation_slots,
                                                    DEV_CREATION_SLOT_HOURS * 60
                                                  )}
                                                </TableCell>
                                                <TableCell className="py-2 text-center tabular-nums text-muted-foreground">
                                                  {formatSlotsAsXxhXX(l.debug_slots, DEV_DEBUG_SLOT_HOURS * 60)}
                                                </TableCell>
                                                <TableCell className="py-2 text-right tabular-nums font-medium">
                                                  {timeEur != null ? `${timeEur} €` : '—'}
                                                </TableCell>
                                              </TableRow>
                                            );
                                          })}
                                        </TableBody>
                                      </Table>
                                    </div>
                                    {groupIncluded[g.key] && devInvoiceProduct ? (
                                      <div className="space-y-2">
                                        <p className="text-xs font-semibold text-foreground">
                                          Aperçu détail journal (l’entrée enregistre 1 ligne ponctuelle / société)
                                        </p>
                                        {g.lines.map((jl) => {
                                          const jk = journalLineKey(jl.entry_id, jl.line_index);
                                          const picked = invoiceLineIncluded[jk] !== false;
                                          if (!picked) return null;
                                          const pl = proposedLineByJournalKey.get(jk);
                                          return (
                                            <div
                                              key={`ent-preview-${jk}`}
                                              className="rounded-md border border-border/80 bg-background/90 p-3 space-y-2"
                                            >
                                              <p className="text-[11px] font-medium text-muted-foreground">
                                                Journal du{' '}
                                                {displayWorkPeriodFr(
                                                  jl.work_date,
                                                  jl.work_date_granularity,
                                                  jl.created_at
                                                ) ?? '—'}{' '}
                                                —{' '}
                                                {(jl.application_name || '').trim() || '—'}
                                              </p>
                                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2 text-xs">
                                                <div>
                                                  <span className="text-muted-foreground block">Catégorie</span>
                                                  <span className="font-medium">
                                                    {pl?.category_name || DEV_INVOICE_CATEGORY_NAME}
                                                  </span>
                                                </div>
                                                <div>
                                                  <span className="text-muted-foreground block">Produit</span>
                                                  <span className="font-medium">
                                                    {pl?.product_name || DEV_INVOICE_PRODUCT_NAME}
                                                  </span>
                                                </div>
                                                <div>
                                                  <span className="text-muted-foreground block">Type</span>
                                                  <span className="font-medium">Ponctuel</span>
                                                </div>
                                                <div>
                                                  <span className="text-muted-foreground block">
                                                    Société (bénéficiaire)
                                                  </span>
                                                  <span className="font-medium">{pl?.company_name || '—'}</span>
                                                </div>
                                                <div>
                                                  <span className="text-muted-foreground block">Établissement</span>
                                                  <span className="font-medium">
                                                    {pl?.establishment_name || '—'}
                                                  </span>
                                                </div>
                                                <div>
                                                  <span className="text-muted-foreground block">Secteur</span>
                                                  <span className="font-medium">{pl?.sector_name || '—'}</span>
                                                </div>
                                                <div>
                                                  <span className="text-muted-foreground block">Montant HT</span>
                                                  <span className="font-medium tabular-nums">
                                                    {pl ? formatDevInvoiceMoney(pl.amount_ht, 'EUR') : '—'}
                                                  </span>
                                                </div>
                                                <div>
                                                  <span className="text-muted-foreground block">TVA</span>
                                                  <span className="font-medium tabular-nums">
                                                    {pl ? `${pl.vat_rate} %` : '—'}
                                                  </span>
                                                </div>
                                                <div>
                                                  <span className="text-muted-foreground block">Montant TVA</span>
                                                  <span className="font-medium tabular-nums">
                                                    {pl ? formatDevInvoiceMoney(pl.amount_vat, 'EUR') : '—'}
                                                  </span>
                                                </div>
                                                <div>
                                                  <span className="text-muted-foreground block">Montant TTC</span>
                                                  <span className="font-medium tabular-nums">
                                                    {pl ? formatDevInvoiceMoney(pl.amount_ttc, 'EUR') : '—'}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : null}
                                    <p className="text-sm text-right font-medium">
                                      Sous-total société (temps + prix prod.) :{' '}
                                      <span className="tabular-nums">{sub != null ? `${sub} €` : '—'}</span>
                                    </p>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <p className="text-sm text-right text-muted-foreground pt-1 border-t border-border/50">
                    Total estimé (sociétés incluses) :{' '}
                    <span className="font-medium text-foreground tabular-nums">
                      {(() => {
                        let sum = 0;
                        let any = false;
                        for (const bg of unbilledCompanyBundles) {
                          if (!groupIncluded[bg.key]) continue;
                          const s = bundleDetailSubtotal(bg);
                          if (s != null) {
                            sum += s;
                            any = true;
                          }
                        }
                        return any ? `${Math.round(sum * 100) / 100} €` : '—';
                      })()}
                    </span>
                  </p>
                </div>
              )}
            </div>

            {invoiceLineItemsPayload.length > 0 && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                {previewLoading ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : null}
                {previewLoading ? 'Mise à jour de l’aperçu…' : preview ? 'Aperçu à jour.' : null}
              </p>
            )}

            {preview && (
              <div className="space-y-4 rounded-lg border border-border p-4 bg-secondary/10">
                <p className="text-sm text-muted-foreground">
                  {preview.lines_to_bill} ligne(s) ·{' '}
                  {preview.ready ? (
                    <span className="text-green-600 font-medium">
                      Développement {preview.total_dev_amount ?? 0} € + mise en production{' '}
                      {preview.total_mep_amount ?? 0} € = {preview.total_amount} €
                    </span>
                  ) : (
                    <span className="text-amber-600 font-medium">
                      Tarifs incomplets — complétez les €/h ci-dessous
                    </span>
                  )}
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Société</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Détail</TableHead>
                      <TableHead>Montant</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(preview.companies || []).map((c) => (
                      <React.Fragment key={c.company_key}>
                        {c.dev && (
                          <TableRow>
                            <TableCell className="align-top font-medium">{c.company_name}</TableCell>
                            <TableCell className="align-top text-muted-foreground">Heures</TableCell>
                            <TableCell className="align-top text-xs text-muted-foreground">
                              {c.dev.creation_hours} h créa · {c.dev.debug_hours} h dbg · {c.dev.line_count} ligne(s)
                            </TableCell>
                            <TableCell className="align-top">
                              {c.dev.amount != null ? `${c.dev.amount} €` : '—'}
                            </TableCell>
                          </TableRow>
                        )}
                        {c.mep && (
                          <TableRow>
                            <TableCell className="align-top font-medium">
                              {c.dev ? '' : c.company_name}
                            </TableCell>
                            <TableCell className="align-top text-muted-foreground">Mise en production</TableCell>
                            <TableCell className="align-top text-xs text-muted-foreground">
                              Temps {c.mep.time_amount != null ? `${c.mep.time_amount} €` : '—'} + production{' '}
                              {c.mep.production_amount} €
                            </TableCell>
                            <TableCell className="align-top">
                              {c.mep.total_amount != null ? `${c.mep.total_amount} €` : '—'}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>

                {!preview.ready && (
                  <div className="space-y-3 border-t border-border pt-4">
                    <p className="text-sm font-medium">Compléter les tarifs (€ / h)</p>
                    {(preview.companies || [])
                      .filter((c) => c.dev?.missing_rates || c.mep?.missing_rates)
                      .map((c) => (
                        <div
                          key={c.company_key}
                          className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end"
                        >
                          <span className="text-sm font-medium pb-2">{c.company_name}</span>
                          <div>
                            <Label className="text-xs">Création</Label>
                            <Input
                              type="number"
                              min={0}
                              step={0.01}
                              value={overrideRates[c.company_key]?.creation ?? ''}
                              onChange={(e) =>
                                setOverrideRates((prev) => ({
                                  ...prev,
                                  [c.company_key]: {
                                    ...prev[c.company_key],
                                    creation: e.target.value,
                                    debug: prev[c.company_key]?.debug ?? '',
                                  },
                                }))
                              }
                              className="bg-background"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Débogage</Label>
                            <Input
                              type="number"
                              min={0}
                              step={0.01}
                              value={overrideRates[c.company_key]?.debug ?? ''}
                              onChange={(e) =>
                                setOverrideRates((prev) => ({
                                  ...prev,
                                  [c.company_key]: {
                                    ...prev[c.company_key],
                                    creation: prev[c.company_key]?.creation ?? '',
                                    debug: e.target.value,
                                  },
                                }))
                              }
                              className="bg-background"
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

              </div>
            )}
            {invoiceWizardStep === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>N° facture (automatique)</Label>
                    <Input
                      readOnly
                      value={entryInvoiceData.invoice_number}
                      placeholder="FA-AAAAMM-DEV-NNNN"
                      className="bg-muted/50"
                    />
                    <p className="text-xs text-muted-foreground">
                      Format <span className="font-mono tabular-nums">FA-AAAAMM-DEV-NNNN</span> (ex.{' '}
                      <span className="font-mono tabular-nums">FA-202603-DEV-0001</span>) — un numéro distinct
                      par société facturée, séquence remise à zéro chaque mois calendaire.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Date de facture *</Label>
                    <Input
                      type="date"
                      value={entryInvoiceData.invoice_date}
                      onChange={(e) => handleDevInvoiceInvoiceDateChange(e.target.value)}
                      className="bg-background"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Devise</Label>
                    <Select
                      value={entryInvoiceData.currency}
                      onValueChange={(v) => handleDevInvoiceCurrencyChange(v)}
                    >
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Devise" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEV_INVOICE_CURRENCIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {entryInvoiceData.currency !== 'EUR' && (
                    <div className="space-y-2">
                      <Label>Taux de change ({entryInvoiceData.currency} → EUR)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.0001"
                          min={0}
                          value={entryInvoiceData.exchange_rate}
                          onChange={(e) =>
                            setEntryInvoiceData((p) => ({
                              ...p,
                              exchange_rate: parseFloat(e.target.value) || 0,
                            }))
                          }
                          className="flex-1 bg-background"
                          disabled={devInvoiceExchangeLoading}
                        />
                        {devInvoiceExchangeLoading ? (
                          <Loader2 className="w-5 h-5 animate-spin shrink-0 text-muted-foreground" />
                        ) : null}
                      </div>
                      {devInvoiceExchangeInfo ? (
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium">
                            1 {entryInvoiceData.currency} = {devInvoiceExchangeInfo.rate?.toFixed(4)} EUR
                          </span>
                          {devInvoiceExchangeInfo.source ? (
                            <span className="ml-1">({devInvoiceExchangeInfo.source})</span>
                          ) : null}
                          {devInvoiceExchangeInfo.warning ? (
                            <span className="block text-amber-600 mt-1">{devInvoiceExchangeInfo.warning}</span>
                          ) : null}
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Le temps interne est valorisé en euros ; en cas de devise autre que l’euro, les montants de facture
                  sont convertis avec le taux indiqué (comme dans le modal Entrées).
                </p>
                <div className="space-y-2">
                  <Label>Fournisseur *</Label>
                  <Combobox
                    options={(devBillingRefs.suppliers || []).map((s) => ({
                      value: s.id,
                      label: s.name || s.id,
                    }))}
                    value={entryInvoiceData.supplier_id}
                    onValueChange={(value) => {
                      const supplier = (devBillingRefs.suppliers || []).find((s) => s.id === value);
                      setEntryInvoiceData((p) => ({
                        ...p,
                        supplier_id: value,
                        supplier_name: supplier?.name || '',
                      }));
                    }}
                    placeholder="Sélectionner le fournisseur…"
                    searchPlaceholder="Rechercher…"
                    emptyText="Aucun fournisseur"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Destinataire (société payeuse) *</Label>
                  <Combobox
                    options={companies.map((c) => ({ value: c.id, label: c.name || c.id }))}
                    value={entryInvoiceData.payer_company_id}
                    onValueChange={(value) => {
                      const co = companies.find((c) => c.id === value);
                      setEntryInvoiceData((p) => ({
                        ...p,
                        payer_company_id: value,
                        payer_company_name: co?.name || '',
                      }));
                    }}
                    placeholder="Sélectionner la société payeuse…"
                    searchPlaceholder="Rechercher…"
                    emptyText="Aucune société"
                  />
                  <p className="text-xs text-muted-foreground">
                    Toujours <strong>CG DEVELOPPEMENT</strong> pour ce flux ; les sociétés à l’étape 1 sont les
                    clients facturés (une facture par société).
                  </p>
                </div>
                {billingCompanyKeysOrdered.length > 0 ? (
                  <div className="space-y-2">
                    <Label>Société pour l’aperçu PDF / pièce jointe</Label>
                    <Select
                      value={invoicePreviewCompanyKey}
                      onValueChange={setInvoicePreviewCompanyKey}
                    >
                      <SelectTrigger className="bg-background max-w-md">
                        <SelectValue placeholder="Choisir une société…" />
                      </SelectTrigger>
                      <SelectContent>
                        {billingCompanyKeysOrdered.map((ck) => (
                          <SelectItem key={ck} value={ck}>
                            {companyNameByBillingKey[ck] || ck}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground max-w-xl">
                      Chaque facture ne comporte qu’un client : l’aperçu et « Joindre » s’appliquent à la société
                      choisie. Avec plusieurs sociétés, utilisez « Tout générer » pour enregistrer toutes les pièces
                      jointes PDF (A4) d’un coup, ou changez de société et répétez manuellement.
                    </p>
                  </div>
                ) : null}
                <div className="space-y-2">
                  <Label>Facture (aperçu & pièce jointe)</Label>
                  <div className="relative min-h-[168px] rounded-xl border-2 border-dashed border-border overflow-hidden bg-muted/10">
                    {devInvoiceAttachLoading || devInvoiceBatchLoading ? (
                      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/88 backdrop-blur-sm gap-4">
                        <div className="relative h-16 w-16 shrink-0">
                          <span className="absolute inset-0 rounded-full border-2 border-primary/25 animate-ping" />
                          <span className="absolute inset-2 rounded-full border-2 border-primary/40 animate-pulse" />
                          <Loader2 className="absolute inset-0 m-auto h-8 w-8 animate-spin text-primary" />
                        </div>
                        <p className="text-sm font-medium text-foreground text-center px-4">
                          {devInvoiceBatchLoading && devInvoiceBatchProgress
                            ? `Génération des factures… ${devInvoiceBatchProgress.current} / ${devInvoiceBatchProgress.total}`
                            : 'Génération du PDF et envoi de la pièce jointe…'}
                        </p>
                      </div>
                    ) : null}
                    <div className="flex flex-col items-center justify-center min-h-[168px] gap-3 p-6 text-center">
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        <Button
                          type="button"
                          variant="default"
                          size="lg"
                          className="gap-2"
                          onClick={handleGenerateDevInvoicePdf}
                          disabled={
                            devInvoiceAttachLoading ||
                            devInvoiceBatchLoading ||
                            billingCompanyKeysOrdered.length === 0
                          }
                        >
                          <FileText className="w-5 h-5" />
                          Générer la facture
                        </Button>
                        {billingCompanyKeysOrdered.length > 1 ? (
                          <Button
                            type="button"
                            variant="secondary"
                            size="lg"
                            className="gap-2"
                            onClick={handleGenerateAllDevInvoices}
                            disabled={
                              devInvoiceAttachLoading ||
                              devInvoiceBatchLoading ||
                              billingCompanyKeysOrdered.length < 2 ||
                              invoiceNumbersReserved.length !== billingCompanyKeysOrdered.length
                            }
                          >
                            <Layers className="w-5 h-5" />
                            Tout générer
                          </Button>
                        ) : null}
                      </div>
                      {billingCompanyKeysOrdered.length > 1 ? (
                        <p className="text-xs text-muted-foreground max-w-lg">
                          « Tout générer » produit et enregistre un <strong>PDF A4</strong> pour{' '}
                          <strong>chaque</strong> société (équivalent à ouvrir l’aperçu puis « Joindre » pour chacune).
                          L’aperçu ci-dessous permet aussi d’imprimer ou d’enregistrer un PDF depuis le navigateur.
                        </p>
                      ) : null}
                      <div className="w-full max-w-lg space-y-2 text-left">
                        {billingCompanyKeysOrdered.map((ck) => {
                          const att = invoiceFilesByCompanyKey[ck];
                          const label = companyNameByBillingKey[ck] || ck;
                          return (
                            <div
                              key={ck}
                              className="flex flex-wrap items-center gap-2 rounded-lg border bg-background px-3 py-2 text-sm"
                            >
                              <span className="font-medium shrink-0 min-w-[6rem]">{label}</span>
                              {att?.file_id ? (
                                <>
                                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                                  <span className="min-w-0 flex-1 truncate" title={att.file_name || ''}>
                                    {att.file_name || 'Pièce jointe'}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 shrink-0"
                                    onClick={() => openDevInvoiceAttachedFile(att.file_id, att.file_name)}
                                    aria-label={`Ouvrir la pièce jointe ${label}`}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
                                    onClick={() => removeDevInvoiceAttachedFile(ck)}
                                    aria-label={`Retirer la pièce jointe ${label}`}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <span className="text-xs text-muted-foreground">Aucune pièce jointe</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {invoiceWizardStep === 3 && (
              <div className="space-y-4">
                <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div className="sm:col-span-2">
                      <span className="text-muted-foreground">N° factures (une par société) :</span>
                      <ul className="mt-1 space-y-0.5 list-none">
                        {billingCompanyKeysOrdered.map((ck, i) => (
                          <li key={ck} className="text-sm">
                            <span className="font-medium">{companyNameByBillingKey[ck] || ck}</span>
                            {' — '}
                            <span className="tabular-nums font-mono">
                              {invoiceNumbersReserved[i] || '—'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Date :</span>{' '}
                      <span className="font-medium">
                        {new Date(entryInvoiceData.invoice_date).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Fournisseur :</span>{' '}
                      <span className="font-medium">{entryInvoiceData.supplier_name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Destinataire :</span>{' '}
                      <span className="font-medium">{entryInvoiceData.payer_company_name}</span>
                    </div>
                    <div className="sm:col-span-2">
                      <span className="text-muted-foreground">Pièces jointes :</span>
                      {billingCompanyKeysOrdered.some((ck) => invoiceFilesByCompanyKey[ck]?.file_id) ? (
                        <ul className="mt-1 space-y-0.5 list-none text-sm">
                          {billingCompanyKeysOrdered.map((ck) => {
                            const att = invoiceFilesByCompanyKey[ck];
                            if (!att?.file_id) return null;
                            return (
                              <li key={ck}>
                                <span className="font-medium">{companyNameByBillingKey[ck]}</span>
                                {' : '}
                                <span>{att.file_name || 'Fichier'}</span>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <span className="text-muted-foreground"> Aucune</span>
                      )}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Devise :</span>{' '}
                      <span className="font-medium">{invoiceWizardDisplay.currency}</span>
                    </div>
                    {invoiceWizardDisplay.currency !== 'EUR' ? (
                      <div>
                        <span className="text-muted-foreground">Taux :</span>{' '}
                        <span className="font-medium">
                          1 {invoiceWizardDisplay.currency} = {invoiceWizardDisplay.rate?.toFixed(4)} EUR
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Produit</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Société</TableHead>
                      <TableHead>Secteur</TableHead>
                      <TableHead className="text-right">HT</TableHead>
                      <TableHead className="text-right">TTC</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoiceWizardDisplay.lines.map((line, index) => (
                      <TableRow key={line.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell className="font-medium">{line.product_name}</TableCell>
                        <TableCell>Ponct.</TableCell>
                        <TableCell>{line.company_name || '—'}</TableCell>
                        <TableCell>{line.sector_name || '—'}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatDevInvoiceMoney(line.d_ht, invoiceWizardDisplay.currency)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatDevInvoiceMoney(line.d_ttc, invoiceWizardDisplay.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="bg-primary/10 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <span className="font-medium">
                      {billingCompanyKeysOrdered.length} ligne(s) en coûts ponctuels (1 par société) — détail{' '}
                      {proposedEntryLines.length} ligne(s) journal
                    </span>
                    <div className="text-right space-y-1">
                      <div className="text-sm">
                        Total HT :{' '}
                        {formatDevInvoiceMoney(
                          invoiceWizardDisplay.totals.ht,
                          invoiceWizardDisplay.currency
                        )}
                      </div>
                      <div className="text-sm">
                        Total TVA :{' '}
                        {formatDevInvoiceMoney(
                          invoiceWizardDisplay.totals.vat,
                          invoiceWizardDisplay.currency
                        )}
                      </div>
                      <div className="text-lg font-bold">
                        Total TTC :{' '}
                        {formatDevInvoiceMoney(
                          invoiceWizardDisplay.totals.ttc,
                          invoiceWizardDisplay.currency
                        )}
                      </div>
                      {invoiceWizardDisplay.currency !== 'EUR' ? (
                        <div className="text-sm text-muted-foreground border-t border-border/60 pt-2 mt-2">
                          Équivalent EUR (TTC) :{' '}
                          {formatDevInvoiceMoney(
                            proposedEntryLines.reduce((s, l) => s + (parseFloat(l.amount_ttc) || 0), 0),
                            'EUR'
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className={`${devModalFooter} flex flex-wrap gap-2 justify-between sm:justify-end`}>
            <Button variant="outline" onClick={() => setInvoiceOpen(false)}>
              Fermer
            </Button>
            <div className="flex flex-wrap gap-2">
              {invoiceWizardStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={invoiceBootstrapLoading || invoiceNumbersLoading}
                  onClick={() => {
                    if (invoiceWizardStep === 2) {
                      setInvoiceNumbersReserved([]);
                      setEntryInvoiceData((p) => ({ ...p, invoice_number: '' }));
                    }
                    setInvoiceWizardStep((s) => Math.max(1, s - 1));
                  }}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
              )}
              {invoiceWizardStep < 3 ? (
                <Button
                  type="button"
                  onClick={goInvoiceWizardNext}
                  disabled={invoiceBootstrapLoading || invoiceNumbersLoading}
                >
                  {invoiceNumbersLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Suivant
                  {!invoiceNumbersLoading ? <ChevronRight className="w-4 h-4 ml-2" /> : null}
                </Button>
              ) : (
                <Button
                  onClick={finalizeDevEntryInvoice}
                  disabled={invoiceBootstrapLoading || saving || !canGenerateDevInvoice}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer la facture'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!devInvoicePreviewPayload}
        onOpenChange={(open) => {
          if (!open) setDevInvoicePreviewPayload(null);
        }}
      >
        <DialogContent
          className={`bg-card border-border max-w-5xl w-[min(100vw-1.5rem,960px)] h-[min(92dvh,900px)] flex flex-col gap-0 p-0 z-[200] ${devModalShell}`}
        >
          <DialogHeader className={`${devModalHeader} px-6 pt-6 pb-2 shrink-0`}>
            <DialogTitle>Aperçu de la facture</DialogTitle>
            <DialogDescription>
              {devInvoicePreviewPayload?.allowAttach === false
                ? 'Facture enregistrée — consultation uniquement.'
                : 'Joindre enregistre un PDF A4 (mise en page identique à l’aperçu) comme pièce jointe. Vous pouvez aussi utiliser Imprimer dans le navigateur (Ctrl+P) depuis l’aperçu.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 px-6 pb-2">
            <iframe
              key={
                devInvoicePreviewPayload
                  ? `${devInvoicePreviewPayload.companyKey || ''}-${devInvoicePreviewPayload.entryHeader?.invoice_number}-${devInvoicePreviewPayload.entryHeader?.invoice_date}`
                  : 'closed'
              }
              title="Aperçu facture développement"
              srcDoc={devInvoicePreviewHtml}
              className="h-full w-full min-h-[320px] rounded-md border border-border bg-white"
              sandbox="allow-same-origin"
            />
          </div>
          <div className="shrink-0 flex flex-row flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-border bg-card">
            {devInvoicePreviewPayload?.allowAttach !== false ? (
              <Button
                type="button"
                onClick={handleAttachDevInvoice}
                disabled={devInvoiceAttachLoading || devInvoiceBatchLoading}
                className="gap-2"
              >
                {devInvoiceAttachLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Paperclip className="h-4 w-4" />
                )}
                Joindre
              </Button>
            ) : null}
            <Button type="button" variant="outline" onClick={() => setDevInvoicePreviewPayload(null)}>
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!devEntryDeleteTarget} onOpenChange={(o) => !o && setDevEntryDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l’entrée développement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Suppression définitive du journal pour « {devEntryDeleteTarget?.label || '…'} » (toutes les lignes de
              temps). Réservé aux administrateurs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={devEntryDeleteLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={devEntryDeleteLoading}
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteDevEntry();
              }}
            >
              {devEntryDeleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
